/* OTK Sakura hero (sections/otk-sakura-hero.liquid): the side niches' pieces, hung face-out on rails.
   1. OTK_CUTOUT: cuts a garment out of its white-ground product photo and tells flat shots from shots on a model.
   2. OTK_HANG: reshapes the cut-out (shaped for an invisible body) so it hangs on its hanger, and paints the drape.
      Both are plain pixel work: the rails run them in a worker made from their own source (hangWorker), off the page.
   3. OTK_SAKURA_RACKS: hangs each side's pieces on two rails, with hangers, a low-key grade, contact shadows, a hover
      tag, and a pendulum sway (scroll speed, the wall's gust, a brushing pointer). Driven by assets/otk-sakura-hero.js.
   Built from the rack lab prototypes (cut-out lab + rack design); styles in assets/otk-sakura-hero.css. */
/*!
 * OTK_CUTOUT - garment cut-out for white-backdrop product photos (Printful mockups).
 * Dependency-free, ES5 syntax, no recursion. Exposes one global: OTK_CUTOUT(imageData, opts).
 *
 *   var r = OTK_CUTOUT(ctx.getImageData(0, 0, w, h), opts);
 *   r === null        nothing usable found (no garment-sized object on a white backdrop)
 *   r.flat            true  -> flat / ghost garment shot: use it
 *                     false -> model shot, cropped close-up, or not a white-backdrop photo
 *   r.score           model-likeness: 0 = clean flat garment, >= 0.5 (opts.threshold) = reject
 *   r.reasons         tags behind the score: 'frame' 'head' 'skin' 'face' 'tall'
 *   r.box {x,y,w,h}   trimmed garment box, SOURCE pixels
 *   r.width, r.height, r.data
 *                     trimmed cut-out, RGBA Uint8ClampedArray (straight alpha, white un-mixed)
 *                     -> new ImageData(r.data, r.width, r.height)
 *   r.hang {x,y,surfaceY}
 *                     hang point in CUT-OUT pixels: x = centre of the opaque pixels in the top 4%
 *                     rows, y = 0 (top); surfaceY = first opaque row at that x (+-2 px)
 *   r.hangSrc {x,y}   the same point in source pixels
 *   r.shoulderY       CUT-OUT row where the silhouette width first reaches 85% of its max
 *                     (shoulder line for tops; ~0 for bottoms whose waistband is the widest part)
 *
 * opts (all optional):
 *   bgMin      246    min(R,G,B) >= bgMin is backdrop for the border flood fill
 *   threshold  0.5    score < threshold -> flat
 *   fast       false  true: return early ({flat:false, data:null, ...}) as soon as the frame cue
 *                     alone rejects the photo (handy when scanning a product's images in order)
 *   classify   false  true: skip building the pixel output (data null); flat/score/box/hang valid
 *   debug      false  true: attach r.features (raw measurements behind the score)
 */
(function otkCutoutLib(root) {
  'use strict';

  var P = 3; // padding ring (backdrop) around the working grid: no bounds checks in the hot loops

  function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
  function zero(a) { if (a.fill) a.fill(0); else for (var z = 0; z < a.length; z++) a[z] = 0; }

  // Scratch buffers are reused between calls of the same size (the function is synchronous, so
  // this is safe); it keeps ~3 MB of allocations and the GC pauses they cause out of each call.
  var POOL = { n: -1 };
  function buffers(PW, PH) {
    var PN = PW * PH;
    if (POOL.n !== PN || POOL.pw !== PW) {
      POOL = { n: PN, pw: PW, dw: new Uint8Array(PN), bg: new Uint8Array(PN), fg: new Uint8Array(PN),
        q: new Int32Array(PN), lab: new Int32Array(PN) };
    } else { zero(POOL.bg); zero(POOL.fg); zero(POOL.lab); }
    return POOL;
  }

  function OTK_CUTOUT(img, opts) {
    opts = opts || {};
    var W = img && img.width | 0, H = img && img.height | 0, src = img && img.data;
    if (!W || !H || !src || src.length < W * H * 4) return null;
    var BG_MIN = opts.bgMin != null ? opts.bgMin : 246;
    var T = 255 - BG_MIN;                   // max distance-from-white of a backdrop pixel
    var THRESH = opts.threshold != null ? opts.threshold : 0.5;
    var PW = W + 2 * P, PH = H + 2 * P, PN = PW * PH;
    var i, j, k, p, x, y, t, u, r, g, b, m, head, tail, sum, hi, rowStart;

    var buf = buffers(PW, PH);
    var dw = buf.dw, bg = buf.bg, fg = buf.fg, q = buf.q, lab = buf.lab;

    // ------------------------------------------------------------------ 1. distance from white
    // dw = 255 - min(R,G,B) on the padded grid; the padding ring stays 0 (= backdrop) forever.
    for (y = 0, p = 0; y < H; y++) {
      i = (y + P) * PW + P;
      for (x = 0; x < W; x++, i++, p += 4) {
        r = src[p]; g = src[p + 1]; b = src[p + 2];
        m = r < g ? r : g; if (b < m) m = b;
        dw[i] = 255 - m;
      }
    }
    // ring = backdrop
    for (i = 0; i < P * PW; i++) { bg[i] = 1; bg[PN - 1 - i] = 1; }
    for (y = P; y < PH - P; y++) { rowStart = y * PW; for (x = 0; x < P; x++) { bg[rowStart + x] = 1; bg[rowStart + PW - 1 - x] = 1; } }

    // ------------------------------------------------------------------ 2. border flood fill (4-conn)
    var sp = 0;
    for (x = 0; x < W; x++) {
      i = P * PW + P + x; if (!bg[i] && dw[i] <= T) { bg[i] = 1; q[sp++] = i; }
      i = (P + H - 1) * PW + P + x; if (!bg[i] && dw[i] <= T) { bg[i] = 1; q[sp++] = i; }
    }
    for (y = 0; y < H; y++) {
      i = (P + y) * PW + P; if (!bg[i] && dw[i] <= T) { bg[i] = 1; q[sp++] = i; }
      i += W - 1; if (!bg[i] && dw[i] <= T) { bg[i] = 1; q[sp++] = i; }
    }
    var hist = new Int32Array(256), nBg = 0;   // backdrop noise histogram (distance from white)
    while (sp > 0) {
      i = q[--sp]; hist[dw[i]]++; nBg++;
      j = i - 1; if (!bg[j] && dw[j] <= T) { bg[j] = 1; q[sp++] = j; }
      j = i + 1; if (!bg[j] && dw[j] <= T) { bg[j] = 1; q[sp++] = j; }
      j = i - PW; if (!bg[j] && dw[j] <= T) { bg[j] = 1; q[sp++] = j; }
      j = i + PW; if (!bg[j] && dw[j] <= T) { bg[j] = 1; q[sp++] = j; }
    }

    // frame contact straight from the flood (used by opts.fast; the score uses the cleaned mask)
    var sT = 0, sB = 0, sL = 0, sR = 0;
    for (x = 0; x < W; x++) { sT += 1 - bg[P * PW + P + x]; sB += 1 - bg[(P + H - 1) * PW + P + x]; }
    for (y = 0; y < H; y++) { sL += 1 - bg[(P + y) * PW + P]; sR += 1 - bg[(P + y) * PW + P + W - 1]; }
    var touch = Math.max(sT / W, sB / W, sL / H, sR / H);
    if (opts.fast && touch >= 0.05) {
      return { flat: false, score: 1, reasons: ['frame'], early: true, box: null, hang: null,
        hangSrc: null, shoulderY: 0, width: 0, height: 0, data: null };
    }

    // ------------------------------------------------------------------ 3. enclosed backdrop pockets
    // Near-white regions NOT reached from the border are kept (linings, white prints, the inside
    // of a collar) unless they are as pure and flat as the backdrop itself: gaps such as
    // arm-holes through which we see the wall. "As pure as the backdrop" = mean distance-from-
    // white <= max(3, backdrop mean + 1.5) and >= 70% of pixels <= max(3, backdrop p90), so the
    // test follows the photo's own JPEG noise. "fg" doubles as the visited mark here (rebuilt in 4).
    var bgMean = 0, bgP90 = 0, acc = 0;
    for (k = 0; k < 256; k++) { bgMean += k * hist[k]; }
    bgMean = nBg ? bgMean / nBg : 0;
    for (k = 0; k < 256; k++) { acc += hist[k]; if (acc >= 0.9 * nBg) { bgP90 = k; break; } }
    var pMean = Math.max(3, bgMean + 1.5), pHi = Math.max(3, bgP90);
    for (y = 0; y < H; y++) {
      i = (y + P) * PW + P;
      for (x = 0; x < W; x++, i++) {
        if (bg[i] || fg[i] || dw[i] > T) continue;
        head = 0; tail = 0; q[tail++] = i; fg[i] = 1; sum = 0; hi = 0;
        while (head < tail) {
          k = q[head++];
          sum += dw[k]; if (dw[k] <= pHi) hi++;
          j = k - 1; if (!fg[j] && !bg[j] && dw[j] <= T) { fg[j] = 1; q[tail++] = j; }
          j = k + 1; if (!fg[j] && !bg[j] && dw[j] <= T) { fg[j] = 1; q[tail++] = j; }
          j = k - PW; if (!fg[j] && !bg[j] && dw[j] <= T) { fg[j] = 1; q[tail++] = j; }
          j = k + PW; if (!fg[j] && !bg[j] && dw[j] <= T) { fg[j] = 1; q[tail++] = j; }
        }
        if (tail >= 16 && sum <= pMean * tail && hi >= 0.7 * tail) {
          for (k = 0; k < tail; k++) bg[q[k]] = 1;
        }
      }
    }

    // ------------------------------------------------------------------ 4. components (8-conn), drop specks
    var sizes = [0], nl = 0, bestSize = 0;
    for (y = 0; y < H; y++) {
      i = (y + P) * PW + P;
      for (x = 0; x < W; x++, i++) {
        if (bg[i] || lab[i]) continue;
        nl++; head = 0; tail = 0; q[tail++] = i; lab[i] = nl;
        while (head < tail) {
          k = q[head++];
          j = k - 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k + 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k - PW - 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k - PW; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k - PW + 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k + PW - 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k + PW; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
          j = k + PW + 1; if (!bg[j] && !lab[j]) { lab[j] = nl; q[tail++] = j; }
        }
        sizes.push(tail);
        if (tail > bestSize) bestSize = tail;
      }
    }
    if (bestSize < W * H * 0.01) return null;
    var keepMin = Math.max(30, bestSize * 0.015);  // specks: < 1.5% of the main garment
    var keep = new Uint8Array(nl + 1);
    for (k = 1; k <= nl; k++) keep[k] = sizes[k] >= keepMin ? 1 : 0;
    var area = 0, minX = W, maxX = -1, minY = H, maxY = -1;
    for (y = 0; y < H; y++) {
      i = (y + P) * PW + P;
      for (x = 0; x < W; x++, i++) {
        if (keep[lab[i]]) {           // lab 0 (backdrop) -> keep[0] = 0
          fg[i] = 1; area++;
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        } else fg[i] = 0;
      }
    }
    var bw = maxX - minX + 1, bh = maxY - minY + 1;

    // ------------------------------------------------------------------ 5. distance to backdrop (chessboard, capped at 9)
    var dist = bg; // reuse: 0 outside the garment, 1..9 inside
    for (i = 0; i < PN; i++) dist[i] = fg[i] ? 9 : 0;
    for (y = minY; y <= maxY; y++) {
      i = (y + P) * PW + P + minX;
      for (x = minX; x <= maxX; x++, i++) {
        if (!dist[i]) continue; m = dist[i];
        if (dist[i - 1] + 1 < m) m = dist[i - 1] + 1;
        if (dist[i - PW - 1] + 1 < m) m = dist[i - PW - 1] + 1;
        if (dist[i - PW] + 1 < m) m = dist[i - PW] + 1;
        if (dist[i - PW + 1] + 1 < m) m = dist[i - PW + 1] + 1;
        dist[i] = m;
      }
    }
    for (y = maxY; y >= minY; y--) {
      i = (y + P) * PW + P + maxX;
      for (x = maxX; x >= minX; x--, i--) {
        if (dist[i] <= 1) continue; m = dist[i];
        if (dist[i + 1] + 1 < m) m = dist[i + 1] + 1;
        if (dist[i + PW + 1] + 1 < m) m = dist[i + PW + 1] + 1;
        if (dist[i + PW] + 1 < m) m = dist[i + PW] + 1;
        if (dist[i + PW - 1] + 1 < m) m = dist[i + PW - 1] + 1;
        dist[i] = m;
      }
    }

    // ------------------------------------------------------------------ 6. classification
    // 6a. frame contact on the cleaned mask: a flat mockup floats on the backdrop; cropped model
    //     shots, detail close-ups and lifestyle photos (no white backdrop at all) hit the frame.
    sT = 0; sB = 0; sL = 0; sR = 0;
    for (x = 0; x < W; x++) { sT += fg[P * PW + P + x]; sB += fg[(P + H - 1) * PW + P + x]; }
    for (y = 0; y < H; y++) { sL += fg[(P + y) * PW + P]; sR += fg[(P + y) * PW + P + W - 1]; }
    touch = Math.max(sT / W, sB / W, sL / H, sR / H);

    // 6b. silhouette width profile (extent of each row) and fill count
    var ext = new Int32Array(bh), cnt = new Int32Array(bh), maxW = 0, l, rr, c;
    for (y = minY; y <= maxY; y++) {
      l = -1; rr = -1; c = 0;
      i = (y + P) * PW + P + minX;
      for (x = minX; x <= maxX; x++, i++) if (fg[i]) { if (l < 0) l = x; rr = x; c++; }
      t = y - minY; ext[t] = l < 0 ? 0 : rr - l + 1; cnt[t] = c;
      if (ext[t] > maxW) maxW = ext[t];
    }
    var top12 = 0, lim12 = Math.max(1, Math.ceil(bh * 0.12));
    for (t = 0; t < lim12; t++) if (ext[t] > top12) top12 = ext[t];
    top12 /= maxW;

    // 6c. head: a solid narrow blob on top (12-50% of max width), then a neck constriction
    //     (< 85% of the blob width), then shoulders >= 1.6x the blob within 30% of the height.
    //     Hoods of flat hoodies widen monotonically into the shoulders -> no neck.
    var neck = 0, neckAt = -1, runMax = 0, below, s, fillC, fillE;
    var lim = Math.floor(bh * 0.4), span = Math.floor(bh * 0.3);
    for (t = 0; t < lim; t++) {
      if (ext[t] > runMax) runMax = ext[t];
      if (t < bh * 0.03 || runMax > 0.5 * maxW || runMax < 0.12 * maxW) continue;
      if (ext[t] < 0.85 * runMax) {
        below = 0;
        for (u = t; u < bh && u < t + span; u++) if (ext[u] > below) below = ext[u];
        if (below > 1.6 * runMax) {
          fillC = 0; fillE = 0;
          for (u = 0; u < t; u++) { fillC += cnt[u]; fillE += ext[u]; }
          if (fillC >= 0.75 * fillE) {          // solid, not a hook or a pair of straps
            s = 1 - ext[t] / runMax;
            if (s > neck) { neck = s; neckAt = t; }
          }
        }
      }
    }

    // 6d. skin: hue ~5-27 deg, saturation 0.18-0.72, R > 95. Gold camo sits near 40 deg and is
    //     mostly darker, so camo garments stay at 1-2.5% while people are 9-50%.
    var skin = 0, skinTop = 0, interior = 0, topLim = minY + bh * 0.25, rb, gb;
    for (y = minY; y <= maxY; y++) {
      i = (y + P) * PW + P + minX; p = (y * W + minX) << 2;
      for (x = minX; x <= maxX; x++, i++, p += 4) {
        if (dist[i] < 2) continue;
        interior++;
        r = src[p]; g = src[p + 1]; b = src[p + 2];
        if (r > 95 && r > g && g >= b) {
          rb = r - b; gb = g - b;
          if (rb >= 0.18 * r && rb <= 0.72 * r && gb <= 0.45 * rb && gb >= 0.1 * rb) {
            skin++; if (y < topLim) skinTop++;
          }
        }
      }
    }
    skin /= Math.max(1, interior); skinTop /= Math.max(1, interior);
    var aspect = bh / bw;

    // 6e. score = model-likeness. Only the frame cue or the head cue can reject on its own;
    //     skin needs a second cue (face-high skin, a head, or a tall silhouette).
    var eClip = clamp01((touch - 0.01) / 0.04);
    var eHead = 0.6 * clamp01(neck / 0.15);
    var skinOk = skin < 0.6;                    // a skin-coloured garment says nothing about people
    var eSkin = skinOk ? 0.45 * clamp01((skin - 0.035) / 0.05) : 0;
    var eFace = skinOk ? 0.3 * clamp01((skinTop - 0.02) / 0.03) : 0;
    var eTall = top12 < 0.5 ? 0.3 * clamp01((aspect - 2.2) / 0.3) : 0;
    var score = Math.round((eClip + eHead + eSkin + eFace + eTall) * 1000) / 1000;
    var reasons = [];
    if (eClip > 0.05) reasons.push('frame');
    if (eHead > 0.05) reasons.push('head');
    if (eSkin > 0.05) reasons.push('skin');
    if (eFace > 0.05) reasons.push('face');
    if (eTall > 0.05) reasons.push('tall');

    var res = {
      flat: score < THRESH, score: score, reasons: reasons,
      box: null, hang: null, hangSrc: null, shoulderY: 0, width: 0, height: 0, data: null
    };
    if (opts.debug) {
      res.features = {
        touch: touch, fgFrac: area / (W * H), aspect: aspect, top12: top12, neck: neck,
        neckAt: neckAt < 0 ? -1 : neckAt / bh, skin: skin, skinTop: skinTop,
        cues: { frame: eClip, head: eHead, skin: eSkin, face: eFace, tall: eTall }
      };
    }

    // ------------------------------------------------------------------ 7. soft edge + white un-mixing
    // Edge band = garment pixels within 2 px of the backdrop. Observed C = a*G + (1-a)*255, so with
    // d = 255 - min(C): a = (d - n0) / (dG - n0), dG = the garment's own distance-from-white taken
    // as the mean over interior pixels (dist >= 3) of the 7x7 window (n0 = 6: JPEG noise floor).
    // Colour is un-mixed from the white: G = (C - (1-a)*255) / a, so no halo on a dark wall.
    var alpha = lab; // reuse: alpha 0..255 inside the garment box
    var tx0 = W, tx1 = -1, ty0 = H, ty1 = -1;
    var eX = [], eY = [], eR = [], eG = [], eB = [];
    var N0 = 6, oy, ox, sumd, nd, mxd, dref, a, inv, aa, wj;
    for (y = minY; y <= maxY; y++) {
      i = (y + P) * PW + P + minX; p = (y * W + minX) << 2;
      for (x = minX; x <= maxX; x++, i++, p += 4) {
        if (!fg[i]) { alpha[i] = 0; continue; }
        aa = 255;
        if (dist[i] <= 2) {
          sumd = 0; nd = 0; mxd = 0;
          for (oy = -3; oy <= 3; oy++) {
            wj = i + oy * PW - 3;
            for (ox = 0; ox < 7; ox++, wj++) {
              if (!fg[wj]) continue;
              if (dist[wj] >= 3) { sumd += dw[wj]; nd++; }
              if (dw[wj] > mxd) mxd = dw[wj];
            }
          }
          dref = nd >= 3 ? sumd / nd : mxd;
          if (dref < 24) dref = 24;
          a = (dw[i] - N0) / (dref - N0);
          if (a < 1) {
            if (a < 0.04) aa = 0;
            else {
              aa = Math.round(a * 255);
              inv = (1 - a) * 255;
              eX.push(x); eY.push(y);
              eR.push((src[p] - inv) / a); eG.push((src[p + 1] - inv) / a); eB.push((src[p + 2] - inv) / a);
            }
          }
        }
        alpha[i] = aa;
        if (aa >= 8) {
          if (x < tx0) tx0 = x; if (x > tx1) tx1 = x;
          if (y < ty0) ty0 = y; if (y > ty1) ty1 = y;
        }
      }
    }
    if (tx1 < 0) return null;
    var ow = tx1 - tx0 + 1, oh = ty1 - ty0 + 1;
    res.box = { x: tx0, y: ty0, w: ow, h: oh };
    res.width = ow; res.height = oh;

    // hang point: centre x of the opaque pixels in the top 4% rows of the trimmed box, y = top
    var hn = Math.max(1, Math.round(oh * 0.04)), hs = 0, hc = 0;
    for (y = 0; y < hn; y++) {
      i = (ty0 + y + P) * PW + P + tx0;
      for (x = 0; x < ow; x++, i++) if (alpha[i] >= 128) { hs += x; hc++; }
    }
    var hx = hc ? Math.round(hs / hc) : (ow >> 1), sy = 0, x2 = Math.min(ow - 1, hx + 2);
    for (y = 0; y < oh && !sy; y++) {
      i = (ty0 + y + P) * PW + P + tx0;
      for (x = Math.max(0, hx - 2); x <= x2; x++) if (alpha[i + x] >= 128) { sy = y || -1; break; }
    }
    res.hang = { x: hx, y: 0, surfaceY: sy < 0 ? 0 : sy };
    res.hangSrc = { x: tx0 + hx, y: ty0 };
    // shoulder line: first row whose silhouette width reaches 85% of the max width
    for (t = 0; t < bh; t++) if (ext[t] >= 0.85 * maxW) { res.shoulderY = Math.max(0, t + minY - ty0); break; }

    if (opts.classify) return res;

    // ------------------------------------------------------------------ 8. pixel output (trimmed)
    var out = new Uint8ClampedArray(ow * oh * 4);
    for (y = 0, k = 0; y < oh; y++) {
      i = (ty0 + y + P) * PW + P + tx0; p = ((ty0 + y) * W + tx0) << 2;
      for (x = 0; x < ow; x++, i++, p += 4, k += 4) {
        aa = alpha[i];
        if (aa) { out[k] = src[p]; out[k + 1] = src[p + 1]; out[k + 2] = src[p + 2]; out[k + 3] = aa; }
      }
    }
    for (j = 0; j < eX.length; j++) {
      x = eX[j] - tx0; y = eY[j] - ty0;
      if (x < 0 || y < 0 || x >= ow || y >= oh) continue;
      k = (y * ow + x) << 2;
      out[k] = eR[j]; out[k + 1] = eG[j]; out[k + 2] = eB[j];   // Uint8ClampedArray clamps + rounds
    }
    res.data = out;
    return res;
  }

  OTK_CUTOUT.lib = otkCutoutLib; // (its source, for the rails' worker: see hangWorker)
  root.OTK_CUTOUT = OTK_CUTOUT;
  if (typeof module !== 'undefined' && module.exports) module.exports = OTK_CUTOUT;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));

/* OTK_HANG - hangs a cut-out on its hanger (the side niches' pieces, below).
   A Printful mockup is shaped as if an invisible person wore it: broad rounded shoulders, sleeves held out from the
   body, a hood standing up, leggings standing with their legs apart and hips filled out. On a hanger the cloth falls
   instead: the shoulders follow the hanger's arms down from the collar, the sleeves drop from the shoulder points (a
   tee's still stand out a little from its sides, a long sleeve hangs almost straight down), a hood falls back behind the
   collar, the legs hang straight down and together from a waistband the clips pull flat, the hips no wider than the
   cloth itself. This reshapes the cut-out once, when it is made, the way the middle rack's shader bends its flat
   textures: a backward-mapped warp (each new pixel looks up where it comes from, bilinear), with the sleeves and the legs
   as layers over the body.
   The print is left alone: the body's middle never moves, a sleeve turns as a whole and never in over any of the print
   (found on each photo: a chest or back piece, lettering down the sides), and a leg or a hip slides only where it has
   no print, so lettering on it moves as a whole. Every move is measured on the silhouette first, capped, and skipped
   when a measurement is missing or odd, so any product photo the owner picks later is safe: at worst it hangs exactly
   as before.

     var h = OTK_HANG(r, { kind: 'top' | 'bottom', cut: 'tee' | 'crop' | ..., hood: true | false, view: 'front' | 'back' })
       r   an OTK_CUTOUT result (data, width, height, hang)
       h   null: nothing to do (keep r); else { data, width, height, plan } at r's pixel scale, trimmed to the garment
     OTK_HANG.drape(h, skip)
           paints the hung cloth's light into h.data: a soft sheen where it is pulled over the hanger's arms and the
           shadow under them, the diagonal folds from the hanger's ends, the sleeves' soft shadow on the body, the clips'
           gathers on a waistband. skip: a mask of pixels to leave as they are (the neck opening, where the walnut goes).
     OTK_HANG.hangPiece(r, cw, it, flat)
           the whole piece as the rails need it (hung, measured, the drape, the walnut baked into a front's opening);
           OTK_HANG.piece(job) cuts the photo out first. Both are plain pixel work: the rails run them in a worker, so
           the page never stalls for them (see hangWorker, below), or on the page when a worker can't be had. */
(function otkHangLib(root) {
  'use strict';

  var DEG = Math.PI / 180;
  var HK = {
    slope: 16,        // the shoulders fall at least this steeply from the collar to the hanger's ends (deg) ...
    slopeMax: 0.035,  // ... the hanger's ends coming down at most this far for it (fraction of the height)
    hood: 0.7,        // a hood keeps this much of its height above where it meets the shoulders, when it can't be told
                      // whether it is seen from the front or from behind;
    hoodFront: 0.85,  // from the front it hardly drops: the hook comes up out of its neck, in front of it (as on the
                      // middle rack), so the eyelets and the opening keep their shape;
    hoodBack: 0.45,   // from behind it lies down on the back: little of it rises above the shoulders (a plain hood; one
                      // with print on it keeps hoodFront, so the print isn't squashed)
    short: 80,        // a short sleeve's outer edge falls this steeply from the shoulder point (deg; 90 = straight down)
    long: 86,         // a long sleeve's
    turn: 26,         // turning a sleeve at most this far (deg) ...
    turnMin: 8,       // ... and, when the full turn would reach the print or close the sleeve against the body, less, in
                      // 2 deg steps down to this; then not at all
    tee: 0.05,        // a short sleeve's tip stays at least this far out past the body's sides below the armpit (fraction
                      // of the width): a tee on a hanger still shows its sleeves standing out from it, a T, not a tunic
    keep: 0.45,       // a sleeve never covers the middle of the body (fraction of the body's half-width), nor the print
    hem: 0.02,        // levelling a top's hem moves it at most this far (fraction of the height)
    droop: 50,        // with no sleeve to turn (a hoodie's arms already hang at its sides) the shoulders round off past
    pin: 0.022,       // the hanger's ends this steeply (deg), and come in this much at the top (fraction of the width)
    legs: 1,          // legs close all of their spread ...
    legsMax: 0.2,     // ... sliding at most this much per px of length (a shear, ~11 deg, only across rows without print)
    hips: 0.15,       // the hips: a side is no wider than the waistband's half plus this much of it at the crotch (and
                      // on at that rate below, until it meets the outline) ...
    hipsMax: 0.3,     // ... coming in at most this much of the side's half-width
    waist: 0.06,      // a waistband straightens by at most this much (fraction of the height) ...
    clips: 0          // ... and comes in this much at its sides (fraction of the width; 0: the clips hold it taut)
  };

  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function ss(t) { t = t < 0 ? 0 : t > 1 ? 1 : t; return t * t * (3 - 2 * t); }
  function lum(d, i) { return 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]; }
  // print: far from the cloth's own lightness (med) or strongly coloured
  function isInk(d, i, med) { return Math.abs(lum(d, i) - med) > 48 || Math.max(d[i], d[i + 1], d[i + 2]) - Math.min(d[i], d[i + 1], d[i + 2]) > 70; }
  // the median lightness of the opaque pixels in rows y0..y1 (every other pixel)
  function lumMedian(d, W, y0, y1, keep) {
    var hist = new Int32Array(256), n = 0, acc = 0, x, y, i, k;
    for (y = y0; y < y1; y += 2) for (x = 0; x < W; x += 2) {
      i = (y * W + x) * 4; if (d[i + 3] < 200 || (keep && !keep(x, y))) continue;
      hist[Math.round(lum(d, i))]++; n++;
    }
    for (k = 0; k < 256; k++) { acc += hist[k]; if (acc >= n / 2) return k; }
    return 0;
  }

  // the silhouette: opaque mask, each row's runs of opaque pixels, each column's first and last opaque row, and the
  // first row to sub-pixel precision (the soft edge's coverage: a shoulder moved by it keeps a clean line, not the
  // photo's staircase)
  function geometry(d, W, H) {
    var A = new Uint8Array(W * H), top = new Int32Array(W), bot = new Int32Array(W), topF = new Float32Array(W), runs = [], area = 0, x, y, i, s;
    for (x = 0; x < W; x++) { top[x] = H; bot[x] = -1; }
    for (y = 0, i = 0; y < H; y++) {
      var rr = [];
      for (x = 0, s = -1; x < W; x++, i++) {
        if (d[i * 4 + 3] >= 128) {
          A[i] = 1; area++;
          if (top[x] === H) top[x] = y;
          bot[x] = y;
          if (s < 0) s = x;
        } else if (s >= 0) { rr.push(s, x - 1); s = -1; }
      }
      if (s >= 0) rr.push(s, W - 1);
      runs.push(rr);
    }
    for (x = 0; x < W; x++) topF[x] = edgeF(d, W, x, top[x], H);
    return { W: W, H: H, A: A, top: top, topF: topF, bot: bot, runs: runs, area: area };
  }
  // the edge over column x whose first half-opaque row is y: y + (1 - its coverage) - the coverage of the two rows above
  function edgeF(d, W, x, y, H) {
    if (y >= H) return H;
    var a0 = d[(y * W + x) * 4 + 3] / 255, a1 = y > 0 ? d[((y - 1) * W + x) * 4 + 3] / 255 : 0, a2 = y > 1 ? d[((y - 2) * W + x) * 4 + 3] / 255 : 0;
    return clamp(y + (1 - a0) - a1 - a2, y - 2, y + 1);
  }

  // the run of row rr that holds x (or comes within 2 px of it): its index, or -1
  function runAt(rr, x) {
    for (var k = 0; k < rr.length; k += 2) if (rr[k] - 2 <= x && rr[k + 1] + 2 >= x) return k;
    return -1;
  }
  function extent(rr) { return rr.length ? rr[rr.length - 1] - rr[0] + 1 : 0; }

  // a straight line e = m * y + k through (ys, es), the outliers dropped (a sleeve's cuff in the rows near a hem):
  // null unless most of the points agree on it
  function fitLine(ys, es) {
    var n = ys.length, keep = [], m = 0, k = 0, i, it;
    if (n < 8) return null;
    for (i = 0; i < n; i++) keep.push(i);
    for (it = 0; it < 3; it++) {
      var c = keep.length, sy = 0, se = 0, syy = 0, sye = 0, res = [], next = [];
      if (c < 6) return null;
      for (i = 0; i < c; i++) { var y = ys[keep[i]], e = es[keep[i]]; sy += y; se += e; syy += y * y; sye += y * e; }
      var den = c * syy - sy * sy;
      m = den ? (c * sye - sy * se) / den : 0; k = (se - m * sy) / c;
      for (i = 0; i < n; i++) res.push(Math.abs(es[i] - (m * ys[i] + k)));
      var lim = Math.max(2.5, 2 * res.slice().sort(function (a, b) { return a - b; })[n >> 1]);
      for (i = 0; i < n; i++) if (res[i] <= lim) next.push(i);
      keep = next;
    }
    return keep.length >= 0.6 * n ? { m: m, k: k } : null;
  }

  // The print: a top's lettering and pictures stand out from its own colour (white or red on a black tee). Measured on
  // cells of ~1.2% of the width: a cell is ink when a fifth of its pixels differ from the garment's median by more than
  // 48 in lightness (or are strongly coloured). All of it counts, wherever it is on the body (a chest piece, a word set
  // down the side, a cross by the hem), out to 0.9 of the way to the body's edge (past that it is the seam's shading);
  // a lone cell (a crease's highlight) doesn't. For each row, how far out from the middle it reaches on each side (px).
  // A pattern all over the cloth (camo, a tie-dye: ink in a tenth of the cells near the sides) isn't something to
  // read: then nothing is returned and only the body's middle is kept clear (HK.keep).
  function artReach(d, g, cx, c0, c1, edgeL, edgeR) {
    var W = g.W, H = g.H, x, y, i, k;
    function inside(x, y) { return x > cx - edgeL(y) + 2 && x < cx + edgeR(y) - 2; }
    var med = lumMedian(d, W, c0, c1 + 1, inside);
    var cs = Math.max(3, Math.round(0.012 * W)), CW = Math.ceil(W / cs), CH = Math.ceil(H / cs), inkN = new Int32Array(CW * CH), totN = new Int32Array(CW * CH);
    for (y = c0; y <= c1; y++) for (x = 0; x < W; x++) {
      i = (y * W + x) * 4; if (d[i + 3] < 200 || !inside(x, y)) continue;
      var c = Math.floor(y / cs) * CW + Math.floor(x / cs);
      totN[c]++;
      if (isInk(d, i, med)) inkN[c]++;
    }
    var ink = new Uint8Array(CW * CH), outer = 0, outerInk = 0, cy, cxx;
    for (cy = 0; cy < CH; cy++) for (cxx = 0; cxx < CW; cxx++) {
      k = cy * CW + cxx; if (totN[k] < 0.3 * cs * cs) continue;
      ink[k] = inkN[k] > 0.2 * totN[k] ? 1 : 0;
      var yc = Math.min(H - 1, cy * cs + (cs >> 1)), u = (cxx * cs + (cs >> 1)) - cx, e = u < 0 ? edgeL(yc) : edgeR(yc);
      if (Math.abs(u) > 0.6 * e) { outer++; outerInk += ink[k]; }
    }
    var out = [new Float32Array(H), new Float32Array(H)];
    if (!outer || outerInk > 0.1 * outer) return out;
    for (cy = 0; cy < CH; cy++) for (cxx = 0; cxx < CW; cxx++) {
      k = cy * CW + cxx; if (!ink[k]) continue;
      var yc2 = Math.min(H - 1, cy * cs + (cs >> 1)), u2 = (cxx * cs + (cs >> 1)) - cx;
      if (Math.abs(u2) > 0.9 * (u2 < 0 ? edgeL(yc2) : edgeR(yc2))) continue;
      var nb = 0;
      for (var oy = -1; oy <= 1 && !nb; oy++) for (var ox = -1; ox <= 1; ox++) {
        var ny = cy + oy, nx = cxx + ox;
        if ((ox || oy) && ny >= 0 && nx >= 0 && ny < CH && nx < CW && ink[ny * CW + nx]) { nb = 1; break; }
      }
      if (!nb) continue;
      var ul = cx - cxx * cs, ur = (cxx + 1) * cs - cx;
      for (y = cy * cs; y < Math.min(H, (cy + 1) * cs); y++) { if (ul > out[0][y]) out[0][y] = ul; if (ur > out[1][y]) out[1][y] = ur; }
    }
    return out;
  }

  // Seen from the front? The photo's own name says so on a Printful mockup (...-front-....jpg, ...-back-....jpg); else a
  // hood's lower part may tell: from the front its opening shows the shaded inside, clearly darker in the middle third
  // than at the sides. Nothing tells a back view as surely (its seam, the light on its crown, a camo print all read
  // either way), so that, or anything unclear, is null.
  function hoodView(d, g, cx, y0, yb) {
    var W = g.W, ya = Math.round(y0 + 0.6 * (yb - y0)), y, x, k, mid = [], sides = [];
    for (y = ya; y < yb; y++) {
      k = runAt(g.runs[y], cx); if (k < 0) continue;
      var a = g.runs[y][k], b = g.runs[y][k + 1], t = (b - a) / 3;
      if (b - a < 12) continue;
      for (x = a + 2; x <= b - 2; x++) {
        var i = (y * W + x) * 4; if (d[i + 3] < 200) continue;
        (x > a + t && x < b - t ? mid : sides).push(lum(d, i));
      }
    }
    if (mid.length < 40 || sides.length < 40) return null;
    function med(v) { v.sort(function (p, q) { return p - q; }); return v[v.length >> 1]; }
    var m = med(mid), s = med(sides);
    return m < 0.82 * s && s - m > 6 ? 'front' : null;
  }

  /* ---------------------------------------------------------------- tops */
  function planTop(g, r, o) {
    var W = g.W, H = g.H, top = g.top, topF = g.topF, runs = g.runs, d = r.data, x, y, k, s, si, u;
    var cx = r.hang ? r.hang.x : W >> 1;
    // the body's run in each row (the one over the collar's centre): the torso from the collar down to the hem
    var ca = new Int32Array(H), cb = new Int32Array(H), c0 = -1, c1 = -1, gap = 0;
    for (y = 0; y < H; y++) { k = runAt(runs[y], cx); ca[y] = k < 0 ? -1 : runs[y][k]; cb[y] = k < 0 ? -1 : runs[y][k + 1]; }
    for (y = 0; y < H; y++) {
      if (ca[y] >= 0) { if (c0 < 0) c0 = y; c1 = y; gap = 0; } else if (c0 >= 0 && ++gap > 3) break;
    }
    if (c0 < 0 || c1 - c0 < 0.3 * H) return null;
    var L = c1 - c0, maxW = 0;
    for (y = 0; y < H; y++) maxW = Math.max(maxW, extent(runs[y]));
    var p = { kind: 'top', W: W, H: H, cx: cx, DY: new Float32Array(W), DD: new Float32Array(W), T0: new Float32Array(W),
      HB: new Float32Array(W), HD: Math.max(4, 0.12 * H), pin: null, hip: null, rot: [], arms: [], label: new Uint8Array(W * H), info: {} };
    for (x = 0; x < W; x++) p.T0[x] = topF[x];
    var noSleeves = /bra|tank/.test(o.cut || '');

    // 1. A hood falls back: it keeps part of its height above where it meets the shoulders (HK.hoodFront seen from
    //    the front, HK.hoodBack from behind). A hood is a narrow top (under 62% of the widest row) that widens suddenly
    //    into the shoulders, 7-32% of the height down.
    var hood = null;
    if (o.hood || o.cut === 'hoodie') {
      var y0 = 0; while (y0 < H && !runs[y0].length) y0++;
      var wTop = extent(runs[Math.min(H - 1, y0 + Math.round(0.06 * H))]);
      if (wTop > 0 && wTop < 0.62 * maxW) {
        for (y = y0 + Math.round(0.05 * H); y < y0 + 0.32 * H; y++) {
          if (extent(runs[y]) > 1.35 * wTop) {
            var rb = runs[y - 1];
            if (y - y0 >= 0.07 * H && rb.length) hood = { yb: y, l: rb[0], r: rb[rb.length - 1] };
            break;
          }
        }
      }
      if (hood) {
        var view = o.view || hoodView(d, g, cx, y0, hood.yb), kh = HK.hood;
        if (view === 'front') kh = HK.hoodFront;
        else if (view === 'back') { // print on the hood (more than a twentieth of it): it keeps its shape
          var hm = lumMedian(d, W, y0, hood.yb), hn = 0, hi = 0;
          for (y = y0; y < hood.yb; y += 2) for (x = hood.l; x <= hood.r; x += 2) {
            var ih = (y * W + x) * 4; if (d[ih + 3] < 200) continue;
            hn++; if (isInk(d, ih, hm)) hi++;
          }
          kh = hi > 0.05 * hn ? HK.hoodFront : HK.hoodBack;
        }
        for (x = 0; x < W; x++) if (top[x] < hood.yb) {
          var hh = hood.yb - topF[x];
          p.DY[x] = (1 - kh) * hh; p.DD[x] = Math.max(1, kh * hh);
        }
        p.info.hood = (1 - kh) * (hood.yb - y0);
        p.info.view = view || '';
        if (view === 'front') p.stemY = hood.yb; // the hook's stem runs down over the hood to its neck (see hangPiece)
      }
    }

    // 2. The sleeves. The torso's sides are straight lines fitted to its lower quarter; going up, the armpit is where
    //    the outline leaves that line. Everything past it is the sleeve: past the line's x above the armpit, outside
    //    the torso's run below it (a long sleeve hanging free). It turns down about the shoulder point (the top of the
    //    outline over the armpit), where the hanger's arm ends.
    var fits = [null, null], sl = noSleeves ? null : [], lowA = Math.round(c1 - 0.25 * L), lowB = Math.round(c1 - 0.03 * L);
    var dl = Math.max(3, 0.02 * W);
    for (si = 0; si < 2; si++) {
      s = si ? 1 : -1;
      var ys = [], es = [];
      for (y = lowA; y <= lowB; y++) if (ca[y] >= 0) { ys.push(y); es.push(s < 0 ? cx - ca[y] : cb[y] - cx); }
      fits[si] = fitLine(ys, es);
    }
    if (!fits[0] || !fits[1]) sl = null;
    function edge(si, y) { var f = fits[si]; return f.m * y + f.k; }
    function edgeL(y) { return edge(0, y); }
    function edgeR(y) { return edge(1, y); }
    for (si = 0; si < 2 && sl; si++) {
      s = si ? 1 : -1;
      var yA = -1;
      for (y = lowA; y > c0 + 0.08 * L + 2; y--) {
        if (ca[y] < 0 || ca[y - 1] < 0 || ca[y - 2] < 0) break;
        var e0 = (s < 0 ? cx - ca[y] : cb[y] - cx) - edge(si, y), e1 = (s < 0 ? cx - ca[y - 1] : cb[y - 1] - cx) - edge(si, y - 1),
          e2 = (s < 0 ? cx - ca[y - 2] : cb[y - 2] - cx) - edge(si, y - 2);
        if (e0 > dl && e1 > dl && e2 > dl) { yA = y; break; }
      }
      // the cut line: straight up from the torso's own edge just under the armpit (no step where the body's side meets it)
      var uA = 0;
      if (yA >= 0) { var yb2 = Math.min(c1, yA + 2); uA = ca[yb2] >= 0 ? clamp(s < 0 ? cx - ca[yb2] : cb[yb2] - cx, edge(si, yA) - 2, edge(si, yA) + dl) : edge(si, yA); }
      var xA = Math.round(cx + s * uA);
      if (yA < 0 || xA < 1 || xA > W - 2 || top[xA] >= yA - 0.04 * H) { sl = null; break; }
      // the sleeve: past the cut line above the armpit; below it, the runs that hang on from there (a long sleeve, a
      // sleeve's hem lower than the armpit), never the torso's own run (its hem's corners stay with the body)
      var Py = topF[xA], n = 0, maxU = 0, tipY = 0, far = 0, list = [], lb = si + 1, sx0 = W, sx1 = 0, sy0 = H, sy1 = 0;
      for (y = 0; y < H; y++) {
        var rr = runs[y];
        for (k = 0; k < rr.length; k += 2) {
          var a0 = rr[k], a1 = rr[k + 1], hit = y <= yA;
          if (s < 0 ? a0 >= cx : a1 <= cx) continue;
          if (!hit) {
            if (a0 - 2 <= cx && a1 + 2 >= cx) continue;
            for (x = Math.max(0, a0 - 1); x <= Math.min(W - 1, a1 + 1) && !hit; x++) hit = p.label[(y - 1) * W + x] === lb;
            if (!hit) continue;
          }
          for (x = a0; x <= a1; x++) {
            u = s * (x - cx);
            if (y <= yA && u <= uA) continue;
            p.label[y * W + x] = lb; n++;
            if (x < sx0) sx0 = x; if (x > sx1) sx1 = x; if (y < sy0) sy0 = y; if (y > sy1) sy1 = y;
            if (u > maxU || (u === maxU && y > tipY)) { maxU = u; tipY = y; }
            var dd = (u - uA) * (u - uA) + (y - Py) * (y - Py); if (dd > far) far = dd;
            if (((x + 3 * y) & 7) === 0) list.push(x, y); // a sample, for the overlap test
          }
        }
      }
      if (n < 0.015 * g.area || maxU - uA < 0.05 * W) { sl = null; break; }
      // the body's sides below the armpit (the fitted line; and the lower quarter as it is, a flared hem's corners), for
      // the T test (the rows just under the armpit may hold the sleeve's underside too)
      var bodyOut = 0;
      for (y = yA; y <= c1; y++) bodyOut = Math.max(bodyOut, edge(si, y));
      for (y = lowA; y <= c1; y++) if (ca[y] >= 0) bodyOut = Math.max(bodyOut, s < 0 ? cx - ca[y] : cb[y] - cx);
      sl.push({ s: s, si: si, xA: xA, uA: uA, Py: Py, yA: yA, beta: Math.atan2(tipY - Py, maxU - uA) / DEG, bodyOut: bodyOut,
        long: Math.sqrt(far) > 0.6 * L, list: list, top: Math.atan2(maxU - uA, tipY - Py),
        box: [Math.max(0, sx0 - 2), Math.min(W - 1, sx1 + 2), Math.max(0, sy0 - 2), Math.min(H - 1, sy1 + 2)] });
    }
    if (!sl) for (k = 0; k < p.label.length; k++) p.label[k] = 0;
    else softLabels(p.label, g.A, d, W, H); // the soft edge goes with its nearest opaque pixel: no outline left behind

    // 3. The shoulders follow the hanger's arms: a straight slope from the collar's side (the outline's highest point
    //    there, or where a hood meets the shoulders) to the hanger's end (the shoulder point over the armpit; without
    //    sleeves, 78% of the way out). The outline is only ever pushed down onto that line (a rounded ghost-mannequin
    //    shoulder above it), never pulled up; the push fades in from the collar and out with depth, so the chest and
    //    back art don't move. (Measured from the edge's sub-pixel position: the new line is clean.)
    var dyT = [0, 0];
    for (si = 0; si < 2 && !noSleeves; si++) {
      s = si ? 1 : -1;
      var N = null, slv = sl ? sl[si] : null;
      if (hood) N = { u: s < 0 ? cx - hood.l : hood.r - cx, y: hood.yb };
      else {
        var best = H;
        for (u = Math.round(0.04 * W); u <= Math.round(0.3 * W); u++) {
          x = cx + s * u; if (x < 0 || x >= W) break;
          if (top[x] < best) { best = top[x]; N = { u: u, y: topF[x] }; }
        }
      }
      var uT = slv ? slv.uA : 0;
      if (!slv) { // without sleeves: the hanger's end 78% of the way out to the side
        var far2 = 0; for (y = 0; y < H; y++) { var rw = runs[y]; if (rw.length) far2 = Math.max(far2, s < 0 ? cx - rw[0] : rw[rw.length - 1] - cx); }
        uT = 0.78 * far2;
      }
      if (!N || uT - N.u < 0.06 * W) continue;
      var xT = Math.round(cx + s * uT), yT = xT >= 0 && xT < W && top[xT] < H ? topF[xT] : H;
      if (yT >= H) continue;
      var dT = clamp((uT - N.u) * Math.tan(HK.slope * DEG) - (yT - N.y), 0, HK.slopeMax * H), yT2 = yT + dT;
      var depth = slv ? clamp(0.7 * (slv.yA - yT), 0.08 * H, 0.22 * H) : 0.16 * H;
      dyT[si] = dT;
      for (u = Math.ceil(N.u); ; u++) {
        x = cx + s * u; if (x < 0 || x >= W) break;
        if (top[x] >= H) continue;
        var tgt, ramp = 1, cap = 0.05 * H;
        if (u <= uT) { tgt = N.y + (u - N.u) / (uT - N.u) * (yT2 - N.y); ramp = ss((u - N.u) / (0.45 * (uT - N.u))); }
        else if (slv) { tgt = topF[x] + dT; p.T0[x] = yT; } // under the turned sleeve: moved with the shoulder point
        else { tgt = yT2 + (u - uT) * Math.tan(HK.droop * DEG); cap = 0.06 * H; }
        var dy = clamp((tgt - topF[x]) * ramp, 0, cap);
        if (dy > p.DY[x]) { p.DY[x] = dy; p.DD[x] = depth; if (u > uT && slv) p.T0[x] = yT; }
      }
      p.arms.push({ s: s, nu: N.u, ny: N.y, tu: uT, ty: yT2, au: slv ? slv.uA : 0.75 * uT, ay: slv ? Math.min(slv.yA, yT2 + 0.22 * H) : yT2 + 0.18 * H, sleeve: !!slv });
    }
    // without sleeves the shoulders also come in a little at the top: the hanger is narrower than a hoodie
    if (!sl && !noSleeves && p.arms.length === 2) {
      var py0 = Math.min(p.arms[0].ty, p.arms[1].ty);
      p.pin = { u0: [0.6 * p.arms[0].tu, 0.6 * p.arms[1].tu], amt: HK.pin * W, y0: py0, y1: py0 + 0.24 * H, cx: cx };
    }
    // the sports bra hangs from its straps, a touch narrower than on a body
    if (/bra/.test(o.cut || '')) p.pin = { u0: [0.3 * maxW, 0.3 * maxW], amt: 0.02 * W, y0: 0.35 * H, y1: -1, cx: cx };

    // 4. The sleeves turn down. Target: the outer edge falls HK.short / HK.long deg from the horizontal, both sides by
    //    the same angle (the smaller), at most HK.turn. A sleeve turns as a whole about the shoulder point, as cloth
    //    hangs from it. It may not reach the print or the body's middle, and a short sleeve must still stand out past
    //    the body's widest row below the armpit (else the tee reads as a tunic): when the full turn breaks either, the
    //    turn is smaller, in 2 deg steps down to HK.turnMin; only then may the sleeve fold a little at the underarm (a
    //    fan: next to the body it turns 3/4 as far); failing all of that it isn't turned.
    if (sl && sl.length === 2) {
      var target = HK.turn;
      for (si = 0; si < 2; si++) target = Math.min(target, Math.max(0, (sl[si].long ? HK.long : HK.short) - sl[si].beta));
      p.info.turn0 = Math.round(target * 10) / 10;
      var pick = null;
      if (target >= 2) {
        var art = artReach(d, g, cx, c0, c1, edgeL, edgeR), turns = [], w0s = [1, 0.75], tt, wi;
        if (o.debug) p.art = art; // (the rack lab draws it)
        for (tt = target; tt >= Math.min(HK.turnMin, target) - 1e-6; tt -= 2) turns.push(tt);
        var fanFor = function (q, si2, t, w0) {
          var f = fanTable(t * DEG, w0, Math.max(q.top, 1.6 * t * DEG * (1 - w0) + 0.02)), reach = 0;
          for (var j = 0; j < q.list.length; j += 2) {
            var ex = q.s * (q.list[j] - q.xA), ey = q.list[j + 1] - q.Py, rr2 = Math.sqrt(ex * ex + ey * ey);
            var ph = f.fwd(Math.atan2(ex, ey)), y2 = q.Py + rr2 * Math.cos(ph), u2 = q.uA + rr2 * Math.sin(ph);
            if (u2 > reach) reach = u2;
            if (y2 > c1 || y2 < c0) continue;
            var yy = Math.round(y2), lim = Math.max(HK.keep * edge(si2, y2), art[si2][yy] ? art[si2][yy] + 0.025 * W : 0);
            if (u2 < lim) return null;
          }
          if (!q.long && reach < q.bodyOut + HK.tee * W) return null;
          return f;
        };
        for (wi = 0; wi < w0s.length && !pick; wi++) for (k = 0; k < turns.length && !pick; k++) {
          var fa = fanFor(sl[0], 0, turns[k], w0s[wi]), fb = fa && fanFor(sl[1], 1, turns[k], w0s[wi]);
          if (fa && fb) pick = { turn: turns[k], w0: w0s[wi], fans: [fa, fb] };
        }
      }
      if (pick) {
        p.info.turn = Math.round(pick.turn * 10) / 10; p.info.w0 = pick.w0;
        for (si = 0; si < 2; si++) {
          var qq = sl[si], fan = pick.fans[si], bx0 = W, bx1 = 0, by0 = H * 3, by1 = 0;
          for (y = qq.box[2]; y <= qq.box[3]; y++) for (x = qq.box[0]; x <= qq.box[1]; x++) {
            if (p.label[y * W + x] !== si + 1) continue;
            var ex2 = qq.s * (x - qq.xA), ey2 = y - qq.Py, r3 = Math.sqrt(ex2 * ex2 + ey2 * ey2), ph2 = fan.fwd(Math.atan2(ex2, ey2));
            var X = qq.xA + qq.s * r3 * Math.sin(ph2), Y = qq.Py + r3 * Math.cos(ph2);
            if (X < bx0) bx0 = X; if (X > bx1) bx1 = X; if (Y < by0) by0 = Y; if (Y > by1) by1 = Y;
          }
          p.rot.push({ type: 'fan', id: si + 1, s: qq.s, px: qq.xA, py: qq.Py, fan: fan, w0: pick.w0,
            bx0: Math.floor(bx0) - 2, bx1: Math.ceil(bx1) + 2, by0: Math.floor(by0) - 2, by1: Math.ceil(by1 + dyT[si]) + 3 });
        }
      } else { p.info.turn = 0; for (k = 0; k < p.label.length; k++) p.label[k] = 0; }
    }

    // 5. The hem hangs level: across the middle 70% of the body, each column's last row moves to the median one
    //    (at most HK.hem of the height), fading out upward.
    if (fits[0] && fits[1]) {
      var hl = Math.round(cx - 0.7 * edge(0, c1)), hr = Math.round(cx + 0.7 * edge(1, c1)), bs = [];
      for (x = Math.max(0, hl); x <= Math.min(W - 1, hr); x++) if (g.bot[x] >= c1 - 0.08 * H) bs.push(g.bot[x]);
      if (bs.length > 0.5 * (hr - hl)) {
        var med = bs.sort(function (a, b) { return a - b; })[bs.length >> 1], tmp = new Float32Array(W), rad = Math.max(1, Math.round(0.03 * W));
        for (x = Math.max(0, hl); x <= Math.min(W - 1, hr); x++) if (g.bot[x] >= c1 - 0.08 * H) tmp[x] = clamp(med - g.bot[x], -HK.hem * H, HK.hem * H);
        for (x = 0; x < W; x++) { // smoothed, and faded out toward the sides of the band
          var sm = 0, cn = 0;
          for (k = x - rad; k <= x + rad; k++) if (k >= 0 && k < W) { sm += tmp[k]; cn++; }
          p.HB[x] = cn ? sm / cn * ss((x - hl) / rad) * ss((hr - x) / rad) : 0;
        }
      }
    }
    p.maxY = H;
    for (k = 0; k < p.rot.length; k++) p.maxY = Math.max(p.maxY, p.rot[k].by1);
    for (x = 0; x < W; x++) if (p.HB[x] > 0) p.maxY = Math.max(p.maxY, g.bot[x] + p.HB[x] + 1);
    p.bodyW = fits[0] && fits[1] ? edge(0, c1) + edge(1, c1) : 0;
    return p;
  }

  // a soft edge pixel (alpha under half) takes the layer of the nearest opaque pixel within 2 px
  function softLabels(lab, A, d, W, H) {
    for (var y = 0; y < H; y++) for (var x = 0; x < W; x++) {
      var i = y * W + x;
      if (A[i] || !d[i * 4 + 3]) continue;
      var best = 9, l = 0;
      for (var oy = -2; oy <= 2; oy++) for (var ox = -2; ox <= 2; ox++) {
        var xx = x + ox, yy = y + oy, dd = Math.abs(ox) + Math.abs(oy);
        if (xx < 0 || yy < 0 || xx >= W || yy >= H || dd >= best || !A[yy * W + xx]) continue;
        best = dd; l = lab[yy * W + xx];
      }
      lab[i] = l;
    }
  }

  // The sleeve's fan: a point at angle ph from straight down (+ outward) about the shoulder point turns to
  // ph - turn * w(ph), w rising from w0 at the body's side (ph 0) to 1 at ph1 and beyond (the sleeve's outer part turns
  // as a whole). Kept monotonic (ph1 >= 1.6 x the turn left to make), so it can be looked up backwards.
  function fanTable(turn, w0, ph1) {
    var M = 1024, lo = -0.75 * Math.PI, hi = Math.PI, st = (hi - lo) / M, fy = new Float32Array(M + 1), ix = new Float32Array(M + 1), i, j;
    function w(ph) { return ph <= 0 ? w0 : w0 + (1 - w0) * ss(ph / ph1); }
    for (i = 0; i <= M; i++) { var ph = lo + st * i; fy[i] = ph - turn * w(ph); }
    // the way back, on its own even grid (the forward map rises steadily, so one walk fills it)
    var blo = fy[0], bst = (fy[M] - blo) / M;
    for (i = 0, j = 0; i <= M; i++) {
      var v = blo + bst * i;
      while (j < M - 1 && fy[j + 1] < v) j++;
      ix[i] = lo + st * (j + clamp((v - fy[j]) / Math.max(1e-9, fy[j + 1] - fy[j]), 0, 1));
    }
    function look(t, a0, step, v) { // linear lookup on an even grid, carried on straight past its ends
      var f = (v - a0) / step;
      if (f <= 0) return t[0] + f * (t[1] - t[0]);
      if (f >= M) return t[M] + (f - M) * (t[M] - t[M - 1]);
      var k = f | 0; return t[k] + (t[k + 1] - t[k]) * (f - k);
    }
    return { fwd: function (ph) { return look(fy, lo, st, ph); }, inv: function (ph) { return look(ix, blo, bst, ph); } };
  }

  /* ---------------------------------------------------------------- bottoms */
  function planBottom(g, r, o) {
    var W = g.W, H = g.H, top = g.top, runs = g.runs, x, y, k, si;
    var cx = r.hang ? r.hang.x : W >> 1;
    var p = { kind: 'bottom', W: W, H: H, cx: cx, DY: new Float32Array(W), DD: new Float32Array(W), T0: new Float32Array(W),
      HB: new Float32Array(W), HD: 1, pin: null, hip: null, rot: [], arms: [], label: new Uint8Array(W * H), info: {} };
    for (x = 0; x < W; x++) p.T0[x] = g.topF[x];
    // the waistband's ends: its width across the top 4%
    var rows = Math.max(2, Math.round(0.04 * H)), tl = W, tr = -1;
    for (y = 0; y < rows; y++) { var rw = runs[y]; if (rw.length) { tl = Math.min(tl, rw[0]); tr = Math.max(tr, rw[rw.length - 1]); } }
    // 1. the legs: below the crotch each row has a gap near the middle; its edges are the legs' inner seams
    var gl = new Float32Array(H), gr = new Float32Array(H), leg = new Uint8Array(H), yc = -1, miss = 0;
    for (y = 0; y < H; y++) {
      var rr = runs[y], bestD = 1e9;
      for (k = 2; k < rr.length; k += 2) {
        var m = (rr[k - 1] + rr[k]) / 2;
        if (Math.abs(m - cx) < 0.2 * W && Math.abs(m - cx) < bestD) { bestD = Math.abs(m - cx); gl[y] = rr[k - 1]; gr[y] = rr[k]; leg[y] = 1; }
      }
    }
    for (y = H - 1; y > 0.15 * H; y--) { if (leg[y]) { yc = y; miss = 0; } else if (++miss > 3) break; }
    if (yc > 0 && H - yc >= 0.15 * H) {
      var ys = [], el = [], er = [];
      for (y = yc; y < H; y++) if (leg[y]) { ys.push(y); el.push(gl[y]); er.push(gr[y]); }
      var fl = fitLine(ys, el), fr = fitLine(ys, er);
      if (fl && fr) {
        // slide each leg in by HK.legs of its seam's spread: a shear, so the hem stays level. It slides only across
        // rows without print on the leg (lettering, a logo: anything far from the leg's own colour), so a print moves
        // as a whole and stays upright; a leg printed all over barely moves.
        var kL = clamp(-fl.m * HK.legs, 0, HK.legsMax), kR = clamp(fr.m * HK.legs, 0, HK.legsMax), b = 0.1 * H;
        p.info.legs = [Math.round(kL * 1000) / 1000, Math.round(kR * 1000) / 1000];
        if (kL > 0.01 || kR > 0.01) {
          var mid = function (y) { return leg[y] ? (gl[y] + gr[y]) / 2 : ((fl.m + fr.m) / 2) * y + (fl.k + fr.k) / 2; };
          for (y = yc; y < H; y++) { var mm = mid(y), o4 = y * W; for (x = 0; x < W; x++) if (r.data[(o4 + x) * 4 + 3]) p.label[o4 + x] = x <= mm ? 1 : 2; }
          var ink = legInk(r.data, g, yc, mid);
          for (si = 0; si < 2; si++) {
            var kk = si ? kR : kL, S = new Float32Array(H + 1);
            for (y = yc + 1; y <= H; y++) {
              var t = y - yc, art = ink[si][Math.min(H - 1, y)];
              S[y] = S[y - 1] + (art ? 0 : kk * (t < b ? t / b : 1));
            }
            p.rot.push({ type: 'shear', id: si + 1, sg: si ? -1 : 1, S: S, bx0: 0, bx1: W - 1, by0: yc - 1, by1: H });
          }
          p.info.inkRows = [ink.n0, ink.n1];
        }
      }
      // 2. the hips (only on a pair with legs: trousers, leggings, shorts)
      if (tr > tl) { p.hip = hips(g, r.data, cx, yc, tl, tr); if (p.hip) p.info.hips = p.hip.info; }
    }
    // 3. the waistband, gripped flat: its top edge straightens to the line through where the clips hold it (0.42 of its
    //    width either side of the middle, as the clip hanger places them), at most HK.waist of the height
    if (tr > tl) {
      var wc = (tl + tr) / 2, half = 0.42 * (tr - tl), xl = Math.round(wc - half), xr = Math.round(wc + half);
      var tgt = (g.topF[clamp(xl, 0, W - 1)] + g.topF[clamp(xr, 0, W - 1)]) / 2, cap = HK.waist * H;
      for (x = tl; x <= tr; x++) {
        if (top[x] >= H) continue;
        var xx = clamp(x, xl, xr), dy = clamp(tgt - g.topF[xx], -cap, cap);
        if (Math.abs(dy) < 0.3) continue;
        p.DY[x] = dy; p.DD[x] = clamp(3 * Math.abs(dy), 0.1 * H, 0.2 * H); // (a bigger move eases out over more rows)
      }
      p.info.waist = Math.round(tgt * 10) / 10;
      if (HK.clips) p.pin = { u0: [0.55 * (wc - tl), 0.55 * (tr - wc)], amt: HK.clips * W, y0: -1, y1: 0.12 * H, cx: Math.round(wc) };
    }
    p.maxY = H;
    if (!p.rot.length && !p.hip && p.info.waist == null) return null;
    return p;
  }

  // The hips. On the mockup an invisible body fills them out to half as wide again as the waistband; hung by its
  // waistband, stretch cloth falls nearly straight. Each side's outline comes in toward a line from the waistband's
  // corner that widens by HK.hips of it down to the crotch (and on at that rate below, until it meets the outline), at
  // most HK.hipsMax of the side's half-width. The outer part of each row slides in as a whole; the cloth between it
  // and the middle third (which stays) takes up the difference. A print near the side (a hip logo) is never in that
  // stretch of cloth: the stretch ends short of it, and across the print's rows the slide holds one value, so the print
  // moves as a whole and stays upright. Printed all over (camo): left as it is. Returns, per side and per row, the
  // slide (D), where the stretch starts (uIn) and where it ends (rEnd), in px from the middle (cx); or null.
  function hips(g, d, cx, yc, tl, tr) {
    var W = g.W, H = g.H, runs = g.runs, y, si, s, u, x, i, k;
    var yw = 0; while (yw < H && !runs[yw].length) yw++;
    if (yc - yw < 0.12 * H) return null;
    var med = lumMedian(d, W, yw, H), win = Math.max(1, Math.round(0.012 * H)), sides = [];
    for (si = 0; si < 2; si++) {
      s = si ? 1 : -1;
      var uw = s < 0 ? cx - tl : tr - cx;
      if (uw < 0.08 * W) return null;
      var e = new Float32Array(H), Dt = new Float32Array(H), uIn = new Float32Array(H), rEnd = new Float32Array(H), inkU = new Float32Array(H), end = H;
      for (y = yw; y < H; y++) { var rr = runs[y]; e[y] = rr.length ? (s < 0 ? cx - rr[0] : rr[rr.length - 1] - cx) : 0; }
      // the stretch: from a third of the way out to the side, over the next 45% (it follows the outline smoothly)
      var rs = Math.max(2, Math.round(0.01 * H));
      for (y = yw; y < H; y++) {
        var se = 0, ne = 0; for (k = Math.max(yw, y - rs); k <= Math.min(H - 1, y + rs); k++) { se += e[k]; ne++; }
        uIn[y] = 0.33 * se / ne; rEnd[y] = uIn[y] + 0.45 * se / ne;
      }
      for (y = yw; y < H; y++) {
        var cap = uw * (1 + HK.hips * (y - yw) / (yc - yw)), dt = clamp(e[y] - cap, 0, HK.hipsMax * e[y]);
        if (y > yc && dt <= 0) { end = y; break; }
        Dt[y] = dt;
      }
      // print in the outer part of each row: the innermost of two print pixels in a row, past the middle third
      var nRows = 0, nInk = 0;
      for (y = yw; y < H; y++) {
        inkU[y] = -1;
        for (u = Math.ceil(uIn[y]), k = 0; u <= e[y]; u++) {
          x = cx + s * u; if (x < 0 || x >= W) break;
          i = (y * W + x) * 4;
          if (d[i + 3] >= 200 && isInk(d, i, med)) { if (++k >= 2) { inkU[y] = u - 1; break; } } else k = 0;
        }
        if (y < end) { nRows++; if (inkU[y] >= 0) nInk++; }
      }
      if (nInk > 0.5 * nRows) return null; // printed all over
      // the print's rows, widened a little (its anti-aliased edge), with the innermost reach across them; the stretch
      // ends short of it
      var blocks = [], a = -1;
      for (y = yw; y < H; y++) {
        var inner = -1;
        for (k = Math.max(yw, y - win); k <= Math.min(H - 1, y + win); k++) if (inkU[k] >= 0 && (inner < 0 || inkU[k] < inner)) inner = inkU[k];
        if (inner >= 0) { rEnd[y] = Math.min(rEnd[y], inner - 3); if (a < 0) a = y; }
        if ((inner < 0 || y === H - 1) && a >= 0) { blocks.push([a, inner < 0 ? y - 1 : y]); a = -1; }
      }
      // smoothed down the rows (twice over ~1% of the height): the outline keeps the photo's own fine edge, and the cloth
      // beside it slides evenly, without a row-to-row jitter combing its pattern
      var rad = Math.max(2, Math.round(0.01 * H)), pass, tmp = new Float32Array(H);
      for (pass = 0; pass < 2 && end > yw; pass++) {
        for (y = yw; y < end; y++) { var sm = 0, cn = 0; for (k = y - rad; k <= y + rad; k++) { sm += Dt[clamp(k, yw, end - 1)]; cn++; } tmp[y] = sm / cn; }
        for (y = yw; y < end; y++) Dt[y] = tmp[y];
      }
      sides.push({ Dt: Dt, uIn: uIn, rEnd: rEnd, blocks: blocks });
    }
    // Both sides slide alike (a pair of hips is symmetric): each row takes the smaller of the two. Across a print's rows
    // the slide holds one value (what its middle row wants, as far as the stretch allows); around a print, and where the
    // outline comes back inside the line (a hem curving in), it changes by at most a quarter px per row, so the outline
    // eases (a short's legs widen a little toward a printed hem) rather than stepping.
    var D = new Float32Array(H), fixd = [], mx = 0;
    for (y = yw; y < H; y++) D[y] = Math.min(sides[0].Dt[y], sides[1].Dt[y]);
    sides.forEach(function (sd) {
      sd.blocks.forEach(function (bk) {
        var lim = 1e9, yy;
        for (yy = bk[0]; yy <= bk[1]; yy++) lim = Math.min(lim, 0.7 * (sd.rEnd[yy] - sd.uIn[yy]));
        fixd.push([bk[0], bk[1], clamp(D[(bk[0] + bk[1]) >> 1], 0, Math.max(0, lim))]);
      });
    });
    fixd.forEach(function (fb) {
      for (var yy = yw; yy < H; yy++) {
        var dist = yy < fb[0] ? fb[0] - yy : yy > fb[1] ? yy - fb[1] : 0;
        D[yy] = Math.min(D[yy], fb[2] + 0.25 * dist);
      }
    });
    for (y = yw + 1; y < H; y++) D[y] = Math.max(D[y], D[y - 1] - 0.25);
    for (y = yw; y < H; y++) {
      for (si = 0; si < 2; si++) D[y] = Math.min(D[y], 0.7 * Math.max(0, sides[si].rEnd[y] - sides[si].uIn[y]));
      if (D[y] > mx) mx = D[y];
    }
    if (mx < 1) return null;
    return { cx: cx, H: H, D: [D, D], uIn: [sides[0].uIn, sides[1].uIn], rEnd: [sides[0].rEnd, sides[1].rEnd], info: Math.round(mx * 10) / 10 };
  }
  // the hips' backward map: where output x (row y) takes its pixel from
  function hipX(hp, x, y) {
    var si = x < hp.cx ? 0 : 1, sg = si ? 1 : -1, yi = y <= 0 ? 0 : y >= hp.H - 1 ? hp.H - 1 : Math.round(y), D = hp.D[si][yi];
    if (D < 0.05) return x;
    var u = sg * (x - hp.cx), a = hp.uIn[si][yi], b = hp.rEnd[si][yi];
    if (u <= a) return x;
    return hp.cx + sg * (u < b - D ? a + (u - a) * (b - a) / (b - D - a) : u + D);
  }

  // which rows of each leg carry print: more than 3% of the leg's pixels in the row (with the rows around it) far from
  // the legs' median lightness (48) or strongly coloured
  function legInk(d, g, yc, mid) {
    var W = g.W, H = g.H, x, y, i, k, s;
    var med = lumMedian(d, W, yc, H);
    var frac = [new Float32Array(H), new Float32Array(H)], out = [new Uint8Array(H), new Uint8Array(H)];
    for (y = yc; y < H; y++) {
      var m = mid(y), cnt = [0, 0], tot = [0, 0];
      for (x = 0; x < W; x++) {
        i = (y * W + x) * 4; if (d[i + 3] < 200) continue;
        s = x <= m ? 0 : 1; tot[s]++;
        if (isInk(d, i, med)) cnt[s]++;
      }
      for (s = 0; s < 2; s++) frac[s][y] = tot[s] ? cnt[s] / tot[s] : 0;
    }
    var win = Math.max(1, Math.round(0.01 * H));
    out.n0 = 0; out.n1 = 0;
    for (s = 0; s < 2; s++) for (y = yc; y < H; y++) {
      for (k = Math.max(yc, y - win); k <= Math.min(H - 1, y + win); k++) if (frac[s][k] > 0.03) { out[s][y] = 1; break; }
      if (out[s][y]) out['n' + s]++;
    }
    return out;
  }

  /* ---------------------------------------------------------------- the warp */
  function sample(d, lab, W, H, sx, sy, id, o) {
    var x0 = Math.floor(sx), y0 = Math.floor(sy), fx = sx - x0, fy = sy - y0, r = 0, g = 0, b = 0, a = 0, j, w, i, al;
    o[0] = 0; o[1] = 0; o[2] = 0; o[3] = 0;
    if (x0 < -1 || y0 < -1 || x0 >= W || y0 >= H) return;
    for (j = 0; j < 4; j++) {
      var xx = x0 + (j & 1), yy = y0 + (j >> 1);
      if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
      i = yy * W + xx;
      if (lab[i] !== id) continue;
      w = ((j & 1) ? fx : 1 - fx) * ((j >> 1) ? fy : 1 - fy);
      if (!w) continue;
      i <<= 2; al = d[i + 3] * w / 255;
      if (!al) continue;
      r += d[i] * al; g += d[i + 1] * al; b += d[i + 2] * al; a += al;
    }
    o[0] = r; o[1] = g; o[2] = b; o[3] = a;
  }

  function render(r, g, p) {
    var W = g.W, H = g.H, d = r.data, lab = p.label, x, y, k;
    var H2 = Math.ceil(Math.max(H, p.maxY + 2)), out = new Uint8ClampedArray(W * H2 * 4), cov = p.rot.length ? new Uint8Array(W * H2) : null;
    var DY = p.DY, DD = p.DD, T0 = p.T0, HB = p.HB, HD = p.HD, bot = g.bot, rot = p.rot, pin = p.pin, hip = p.hip, px = [0, 0, 0, 0];
    for (y = 0; y < H2; y++) {
      for (x = 0; x < W; x++) {
        // the column's vertical move (shoulders, hood, waistband at the top; the hem at the bottom)
        var yv = y, dy = DY[x], hb = HB[x];
        if (dy) yv -= dy * (1 - ss((y - T0[x] - dy) / DD[x]));
        if (hb) yv -= hb * (1 - ss((bot[x] + hb - y) / HD));
        var xb = x;
        if (pin && yv > pin.y0) { // the sides come in: the outer part of the row reaches out further for its pixels
          var si = x < pin.cx ? 0 : 1, uu = si ? x - pin.cx : pin.cx - x, u0 = pin.u0[si];
          if (uu > u0) {
            var amt = pin.amt * (pin.y1 < 0 ? ss((yv - pin.y0) / (0.1 * H)) : 1 - ss((yv - pin.y0) / (pin.y1 - pin.y0)));
            var c = amt / Math.max(1, (W / 2 - u0) * (W / 2 - u0));
            xb = x + (si ? 1 : -1) * c * (uu - u0) * (uu - u0);
          }
        }
        if (hip) xb = hipX(hip, xb, yv);
        if (yv === y && xb === x && y < H) { // unmoved (most of the body): the pixel itself
          var j0 = y * W + x, a0 = lab[j0] ? 0 : d[j0 * 4 + 3] / 255;
          px[0] = d[j0 * 4] * a0; px[1] = d[j0 * 4 + 1] * a0; px[2] = d[j0 * 4 + 2] * a0; px[3] = a0;
        } else sample(d, lab, W, H, xb, yv, 0, px);
        var cr = px[0], cg = px[1], cbb = px[2], ca = px[3];
        for (k = 0; k < rot.length; k++) {
          var q = rot[k], sx, sy;
          if (x < q.bx0 || x > q.bx1 || y < q.by0 || y > q.by1) continue;
          if (q.type === 'fan') {
            var ex = q.s * (x - q.px), ey = yv - q.py, rr = Math.sqrt(ex * ex + ey * ey), ph = q.fan.inv(Math.atan2(ex, ey));
            sx = q.px + q.s * rr * Math.sin(ph); sy = q.py + rr * Math.cos(ph);
          } else { // a leg: its row's slide, from the table (then the hips, as on the body)
            var yy = yv < 0 ? 0 : yv > H ? H : yv, y0i = Math.floor(yy), fy = yy - y0i, y1i = Math.min(H, y0i + 1);
            sx = x - q.sg * (q.S[y0i] + (q.S[y1i] - q.S[y0i]) * fy); sy = yv;
            if (hip) sx = hipX(hip, sx, sy);
          }
          sample(d, lab, W, H, sx, sy, q.id, px);
          var a = px[3];
          if (!a) continue;
          // over the body; where the two meet edge to edge (a sleeve's seam) their coverage adds up, no see-through line
          var both = ca > 0 && a < 1 ? Math.min(1, a + ca) : 0;
          cr = px[0] + cr * (1 - a); cg = px[1] + cg * (1 - a); cbb = px[2] + cbb * (1 - a);
          var over = a + ca * (1 - a);
          if (both > over) { var f = both / over; cr *= f; cg *= f; cbb *= f; over = both; }
          ca = over;
          if (cov) cov[y * W + x] = Math.max(cov[y * W + x], Math.round(a * 255));
        }
        if (ca > 0.003) {
          var o = (y * W + x) * 4;
          out[o] = cr / ca; out[o + 1] = cg / ca; out[o + 2] = cbb / ca; out[o + 3] = Math.min(255, ca * 255);
        }
      }
    }
    // trim to the garment
    var x0 = W, x1 = -1, y0 = H2, y1 = -1;
    for (y = 0; y < H2; y++) for (x = 0; x < W; x++) if (out[(y * W + x) * 4 + 3] >= 8) {
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    if (x1 < 0) return null;
    var w2 = x1 - x0 + 1, h2 = y1 - y0 + 1, data = new Uint8ClampedArray(w2 * h2 * 4), cv = cov ? new Uint8Array(w2 * h2) : null;
    for (y = 0; y < h2; y++) {
      data.set(out.subarray(((y + y0) * W + x0) * 4, ((y + y0) * W + x1 + 1) * 4), y * w2 * 4);
      if (cv) cv.set(cov.subarray((y + y0) * W + x0, (y + y0) * W + x1 + 1), y * w2);
    }
    // the plan's points in the trimmed frame, for the drape; where a front hood's neck now is, for the hook's stem
    p.arms.forEach(function (a) { a.ox = x0; a.oy = y0; });
    p.ox = x0; p.oy = y0; p.cov = cv; if (!p.debug) { p.label = null; p.T0 = null; }
    if (p.stemY != null) p.info.stem = clamp((p.stemY - y0) / h2, 0, 0.4);
    return { data: data, width: w2, height: h2, plan: p, hk: h2 / H, bf: p.bodyW ? clamp(p.bodyW / w2, 0.4, 1) : 0 };
  }

  function OTK_HANG(r, o) {
    o = o || {};
    if (!r || !r.data || r.width < 40 || r.height < 40) return null;
    var g = geometry(r.data, r.width, r.height), p = null;
    try { p = o.kind === 'bottom' ? planBottom(g, r, o) : planTop(g, r, o); } catch (e) { p = null; if (o.debug) throw e; }
    if (p && o.debug) p.debug = true; // (the rack lab: keep the layers to draw them, and throw what goes wrong)
    return p ? render(r, g, p) : null;
  }

  /* ---------------------------------------------------------------- the drape's light */
  // Two maps over the cut-out: M darkens (multiplied in, by no more than a dark cloth's own darkness: a light shirt gets
  // a soft fold, not a grey bar), S lightens (screened in toward the cove's warm light, so it shows on black cloth too,
  // where darkening alone would be lost).
  // A crease: a dark trough along a segment with a lit ridge on its upper side, strongest near its start.
  function crease(M, S, W, H, ax, ay, bx, by, sig, str, under, ridge) {
    var vx = bx - ax, vy = by - ay, len2 = vx * vx + vy * vy, len = Math.sqrt(len2), x, y;
    if (!len) return;
    var nx = vy / len, ny = -vx / len;
    if (ny > 0) { nx = -nx; ny = -ny; } // the normal points up: the ridge is on the lit side
    var pad = 4 * sig, X0 = Math.max(0, Math.floor(Math.min(ax, bx) - pad)), X1 = Math.min(W - 1, Math.ceil(Math.max(ax, bx) + pad));
    var Y0 = Math.max(0, Math.floor(Math.min(ay, by) - pad)), Y1 = Math.min(H - 1, Math.ceil(Math.max(ay, by) + pad));
    for (y = Y0; y <= Y1; y++) for (x = X0; x <= X1; x++) {
      var t = ((x - ax) * vx + (y - ay) * vy) / len2;
      if (t < 0 || t > 1) continue;
      var q = ((x - ax) * nx + (y - ay) * ny) / sig, i = y * W + x;
      var env = ss(t / 0.15) * Math.pow(1 - t, 1.3) * str * (under ? 1 - under[i] : 1);
      if (env < 0.002) continue;
      M[i] *= 1 - env * Math.exp(-q * q);
      S[i] = 1 - (1 - S[i]) * (1 - ridge * env * Math.exp(-(q - 1.8) * (q - 1.8)));
    }
  }

  var LIGHT = [255, 226, 190]; // the cove's warm light
  OTK_HANG.drape = function (h, skip) {
    var p = h && h.plan, W = h.width, H = h.height, d = h.data, x, y, i, k;
    if (!p) return;
    var M = new Float32Array(W * H), S = new Float32Array(W * H), tD = new Float32Array(W);
    for (i = 0; i < M.length; i++) M[i] = 1;
    for (x = 0; x < W; x++) { for (y = 0; y < H && d[(y * W + x) * 4 + 3] < 128; y++); tD[x] = edgeF(d, W, x, y, H); }
    var cov = p.cov, ox = p.ox, oy = p.oy, cx = p.cx - ox, covB = null;
    if (p.kind === 'top') {
      // the sleeves' coverage, feathered: their shadow on the body and the folds under them fade in at their edge
      if (cov) { covB = blur(cov, W, H, Math.max(1, Math.round(0.006 * W))); for (i = 0; i < covB.length; i++) covB[i] /= 255; }
      var hw = 0.05 * W, sig = Math.max(1.1, 0.009 * W);
      p.arms.forEach(function (a) {
        var s = a.s, nu = a.nu, tu = a.tu;
        // the cloth pulled over the hanger's arm: a broad soft sheen a little under the shoulder line, the shadow under it
        for (var u = Math.floor(nu); u <= tu + 0.05 * W; u++) {
          x = Math.round(cx + s * u); if (x < 0 || x >= W || tD[x] >= H) continue;
          var wu = ss((u - nu) / (0.3 * (tu - nu))) * (1 - ss((u - tu) / (0.05 * W)));
          for (y = Math.floor(tD[x]); y < Math.min(H, tD[x] + 4.5 * hw); y++) {
            var v = (y + 0.5 - tD[x]) / hw, hi = Math.exp(-(v - 1) * (v - 1) / 0.5), lo = Math.exp(-(v - 2.6) * (v - 2.6) / 0.8);
            i = y * W + x;
            S[i] = 1 - (1 - S[i]) * (1 - 0.07 * wu * hi);
            M[i] *= 1 - 0.24 * wu * lo;
          }
        }
        // diagonal folds from the hanger's end toward the underarm
        var Tx = cx + s * tu, Ty = a.ty - oy, Ax = cx + s * a.au, Ay = a.ay - oy;
        crease(M, S, W, H, Tx - s * 0.01 * W, Ty + 0.03 * H, Ax - s * 0.06 * W, Ay + 0.03 * H, sig, 0.4, covB, 0.32);
        crease(M, S, W, H, Tx - s * 0.04 * W, Ty + 0.05 * H, Ax - s * 0.13 * W, Ay + 0.08 * H, sig * 1.3, 0.25, covB, 0.32);
      });
      // the sleeves stand a little off the body: their soft shadow on it, down and in
      if (cov) {
        var rad = Math.max(1, Math.round(0.012 * W)), dy2 = Math.round(0.008 * H), X0 = W, X1 = -1, Y0 = H, Y1 = -1;
        for (y = 0; y < H; y++) for (x = 0; x < W; x++) if (cov[y * W + x]) { if (x < X0) X0 = x; if (x > X1) X1 = x; if (y < Y0) Y0 = y; if (y > Y1) Y1 = y; }
        if (X1 >= 0) {
          X0 = Math.max(0, X0 - 3 * rad); X1 = Math.min(W - 1, X1 + 3 * rad); Y0 = Math.max(0, Y0 - 3 * rad); Y1 = Math.min(H - 1, Y1 + 3 * rad + dy2);
          var bw = X1 - X0 + 1, bh = Y1 - Y0 + 1, sub = new Uint8Array(bw * bh);
          for (y = 0; y < bh; y++) sub.set(cov.subarray((y + Y0) * W + X0, (y + Y0) * W + X1 + 1), y * bw);
          var bl = blur(sub, bw, bh, rad);
          for (y = dy2; y < bh; y++) for (x = 0; x < bw; x++) {
            var sh = bl[(y - dy2) * bw + x] / 255; i = (y + Y0) * W + x + X0;
            if (sh > 0.004) M[i] *= 1 - 0.45 * sh * (1 - covB[i]);
          }
        }
      }
    } else {
      // the clips gather the waistband: three short folds fanning down from each
      var rows = Math.max(2, Math.round(0.04 * H)), tl = W, tr = -1;
      for (y = 0; y < rows; y++) for (x = 0; x < W; x++) if (d[(y * W + x) * 4 + 3] >= 128) { if (x < tl) tl = x; if (x > tr) tr = x; }
      if (tr > tl) {
        var wc = (tl + tr) / 2, half = 0.42 * (tr - tl), sg = Math.max(1, 0.012 * W);
        for (k = -1; k <= 1; k += 2) {
          var xc = wc + k * half, yt = tD[clamp(Math.round(xc), 0, W - 1)];
          for (var f = -1; f <= 1; f++) crease(M, S, W, H, xc + f * 0.014 * W, yt + 0.01 * H, xc + f * 0.05 * W - k * 0.012 * W, yt + 0.1 * H, sg, f ? 0.35 : 0.5, null, 0.7);
        }
      }
    }
    for (i = 0; i < M.length; i++) {
      if ((M[i] === 1 && !S[i]) || !d[i * 4 + 3] || (skip && skip[i])) continue;
      for (k = 0; k < 3; k++) { var c = d[i * 4 + k], c2 = c - Math.min(c, 72) * (1 - M[i]); d[i * 4 + k] = c2 + (LIGHT[k] - c2) * S[i]; }
    }
  };

  // a box blur (twice, both ways) of a coverage map
  function blur(src, W, H, rad) {
    var a = new Float32Array(W * H), b = new Float32Array(W * H), x, y, k, pass, s;
    for (k = 0; k < src.length; k++) a[k] = src[k];
    for (pass = 0; pass < 2; pass++) {
      for (y = 0; y < H; y++) { s = 0; var o = y * W;
        for (x = -rad; x < W + rad; x++) { if (x + rad < W && x + rad >= 0) s += a[o + x + rad]; if (x - rad - 1 >= 0 && x - rad - 1 < W) s -= a[o + x - rad - 1]; if (x >= 0 && x < W) b[o + x] = s / (2 * rad + 1); } }
      for (x = 0; x < W; x++) { s = 0;
        for (y = -rad; y < H + rad; y++) { if (y + rad < H && y + rad >= 0) s += b[(y + rad) * W + x]; if (y - rad - 1 >= 0 && y - rad - 1 < H) s -= b[(y - rad - 1) * W + x]; if (y >= 0 && y < H) a[y * W + x] = s / (2 * rad + 1); } }
    }
    return a;
  }

  /* ---------------------------------------------------------------- the piece, as the rails hang it */
  // The cut-out, hung (OTK_HANG, above) and measured again on its new shape: the hook goes where its collar now is, the
  // clips where its waistband is, and the walnut into its neck opening. flat: as photographed (the rack lab compares
  // the two). cw: the photo's width at the cut-out's scale (bakeBar's sizes are in it). it: { kind, cut, title, url }.
  // Returns the pixels and what the rails need to place them: ar (aspect), ar0 (as photographed), waist (the top's
  // width), hx / neck (the collar's centre and its first row), bar (the walnut's top: a front with a neck opening), stem
  // (the hook's stem over a hood seen from the front: where its neck is), fw, hk (the box's height for the same scale),
  // bf (the body's share of the width), hung.
  function hangPiece(r, cw, it, flat) {
    // Hung: its box may now be taller (sleeves hanging lower) or shorter (a hood fallen back) at the same scale (hk),
    // and the body fills more of its width (bf). If anything goes wrong it hangs as it was.
    // The walnut: only ever in the photo's own neck opening, looked for on the photo first (a top, not a hood: none on a
    // hood, as on the middle rack). The hung cut-out must still show that opening, near its collar, or the photo is kept
    // as it is; and a hung shape never gets an opening the photo didn't have (a print pulled up into the collar's
    // search band by a hood that fell back is not a neck opening).
    it = it || {};
    // (a hood: its title says so, or the photo's name: Printful's ...-hoodie-black-back-....jpg, as on T.O.P. CHAMP)
    var hung = null, hk = 1, bf = 0, ar0 = r.width / r.height, plan = null, hooded = /hood/i.test(it.title || '') || /hoodie/i.test(fileOf(it.url));
    var win0 = it.kind === 'top' && !hooded && r.hang ? neckWindow(r) : null, win = null;
    if (!flat) {
      try { hung = OTK_HANG(r, { kind: it.kind, cut: it.cut, hood: hooded, view: viewOf(it.url) }); } catch (e) { hung = null; }
      if (hung) {
        plan = hung.plan.info;
        var r1 = { data: hung.data, width: hung.width, height: hung.height, hang: hangPoint(hung.data, hung.width, hung.height) };
        if (plan.hood != null) win0 = null;
        win = win0 ? neckWindow(r1) : null;
        if (win && (win.top - r1.hang.surfaceY) / r1.height > Math.max(0.12, (win0.top - r.hang.surfaceY) / r.height + 0.04)) win = null;
        if (win0 && !win) { hung = null; plan = null; }
        else { r = r1; hk = hung.hk; bf = hung.bf; }
      }
    }
    if (!hung) win = win0;
    // the waistband / collar: the garment's width across its top 4%
    var d = r.data, rows = Math.max(2, Math.round(r.height * 0.04)), tl = r.width, tr = -1, x, y;
    for (y = 0; y < rows; y++) for (x = 0; x < r.width; x++) if (d[(y * r.width + x) * 4 + 3] > 128) { if (x < tl) tl = x; if (x > tr) tr = x; }
    var waist = tr >= tl ? (tr - tl + 1) / r.width : 0;
    // the collar: where a hanger's neck goes in. Over the hang point (the centre of the top 4%), the first opaque row,
    // so the hook meets the cloth even where the neckline dips below the box top (fractions of the cut-out)
    var hx = r.hang ? r.hang.x / r.width : 0.5, neck = r.hang ? r.hang.surfaceY / r.height : 0;
    // the drape's light goes on first, around the opening (its lining is shaded by bakeBar as the inside of a garment)
    if (hung) try { OTK_HANG.drape(hung, win ? win.mask : null); } catch (e) { /* hung, without the drape's light */ }
    var barY = win ? bakeBar(r, win, cw) : null;
    // a front hood's stem: down to where the hood meets the shoulders, no further than 15% of the height below its top
    // (a hood that lies on the shoulders meets them low; the neck is about there)
    var stem = plan && plan.stem > neck + 0.02 ? Math.min(plan.stem, neck + 0.15) : null;
    return { data: r.data, width: r.width, height: r.height, ar: r.width / r.height, ar0: ar0, waist: waist, hx: hx,
      neck: Math.min(0.2, neck), bar: barY, stem: barY == null ? stem : null, fw: cw / r.width, hk: hk, bf: bf, hung: !!hung, plan: plan,
      hood: hooded || !!(plan && plan.hood != null) };
  }
  // the view the photo's own name gives (Printful: ...-black-back-6826424f4117c.jpg, ...-white-left-front-...)
  function fileOf(url) { url = String(url || ''); return /^data:/.test(url) ? '' : url.split('?')[0].split('/').pop().toLowerCase(); }
  function viewOf(url) {
    var f = fileOf(url);
    return /(^|[-_])back([-_.]|$)/.test(f) ? 'back' : /(^|[-_])front([-_.]|$)/.test(f) ? 'front' : null;
  }

  // the hang point, as OTK_CUTOUT measures it: the centre of the opaque pixels across the top 4% rows, and the first
  // opaque row over it (+-2 px)
  function hangPoint(d, W, H) {
    var hn = Math.max(1, Math.round(H * 0.04)), hs = 0, hc = 0, x, y, sy = -1;
    for (y = 0; y < hn; y++) for (x = 0; x < W; x++) if (d[(y * W + x) * 4 + 3] >= 128) { hs += x; hc++; }
    var hx = hc ? Math.round(hs / hc) : W >> 1;
    for (y = 0; y < H && sy < 0; y++) for (x = Math.max(0, hx - 2); x <= Math.min(W - 1, hx + 2); x++) if (d[(y * W + x) * 4 + 3] >= 128) { sy = y; break; }
    return { x: hx, y: 0, surfaceY: Math.max(0, sy) };
  }

  // A front-facing top shows the inside of its back through the neck opening (the light lining of a Printful mockup). On
  // a rail the hanger's wooden bar crosses that opening: in front of the lining, behind the front of the garment. This
  // finds the opening on the cut-out: from the collar's centre (the hang point), down to the first run of light, neutral
  // pixels, then a flood fill over pixels like it (close in lightness, near-white, opaque). It must be a pocket inside the
  // garment: below its upper part it may not touch the outside (a sports bra's lining is open at the top, between the
  // straps) nor run off the middle of the garment; a fill that does is the garment's own light cloth or print (a white
  // tee). Back views and hoodies have no light lining at the collar, so they find nothing. Returns the opening's mask
  // and its bounds (cut-out pixels).
  function neckWindow(r) {
    var W = r.width, H = r.height, d = r.data, cx = r.hang.x, y0 = r.hang.surfaceY;
    var yMax = Math.round(H * 0.8), xMin = Math.max(0, Math.round(cx - W * 0.45)), xMax = Math.min(W - 1, Math.round(cx + W * 0.45));
    function px(x, y) { var k = (y * W + x) * 4; return [d[k], d[k + 1], d[k + 2], d[k + 3]]; }
    function lum(c) { return 0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]; }
    function chroma(c) { return Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]); }
    function light(c) { return c[3] > 200 && lum(c) >= 170 && chroma(c) < 30; }
    var sy = -1, y, x, j;
    for (y = y0; y < Math.round(H * 0.3) - 3; y++) {
      if (light(px(cx, y)) && light(px(cx, y + 1)) && light(px(cx, y + 2)) && light(px(cx, y + 3))) { sy = y; break; }
    }
    if (sy < 0) return null;
    var L0 = lum(px(cx, sy)), mask = new Uint8Array(W * H), stack = [cx, sy], n = 0, top = H, bot = 0, left = W, right = 0;
    var edgeY = [], bound = false;
    mask[sy * W + cx] = 1;
    while (stack.length) {
      y = stack.pop(); x = stack.pop(); n++;
      if (y < top) top = y; if (y > bot) bot = y; if (x < left) left = x; if (x > right) right = x;
      var nb = [x - 1, y, x + 1, y, x, y - 1, x, y + 1];
      for (j = 0; j < 8; j += 2) {
        var ax = nb[j], ay = nb[j + 1];
        if (ax < xMin || ax > xMax || ay < 0 || ay >= yMax) { bound = true; continue; }
        if (mask[ay * W + ax]) continue;
        var c = px(ax, ay);
        if (c[3] <= 200) { if (j !== 4) edgeY.push(y); continue; } // the outside straight above is fine
        if (chroma(c) < 32 && lum(c) >= 140 && Math.abs(lum(c) - L0) < 50) { mask[ay * W + ax] = 1; stack.push(ax, ay); }
      }
    }
    var edge = 0, ww = (right - left + 1) / W;
    edgeY.forEach(function (ey) { if (ey > top + 0.4 * (bot - top)) edge++; });
    if (bound || edge > Math.max(4, 0.1 * Math.sqrt(n)) || n < W * H * 0.002 || ww < 0.08 || ww > 0.7) return null;
    return { mask: mask, top: top, bot: bot, left: left, right: right };
  }

  // The middle rack's hanger, painted into the opening as the Armory lab bakes its fronts (otk-armory-lab
  // scripts/pack_assets.py, bake_neck_opening + wood_shade, the look the owner chose): a walnut bar whose curved top sits
  // 40% of the way down from the back band's top to the front neckline, following the shoulders' slope down to the
  // sides, and fills the opening from there down to the front neckline, so the front of the garment covers its lower part
  // and its ends: the hanger is inside. Lit top edge, darker underside, a little grain, darker still near the front
  // collar; a soft shadow on the lining just above it. A deep opening (a scoop, a V, a sports bra's open back) gets the
  // lab's "deep" fix: the wood just under the back band, as tall as on a crew. The lining is shaded as the inside of a
  // garment is, deeper down darker. Sizes are the lab's, in px of a 900 px Printful frame (F: the frame's width here).
  // Returns where the wood's top meets the stem at the centre (fraction of the cut-out's height).
  var WOOD = [112, 70, 40], WOOD_HI = [178, 124, 80], WOOD_LO = [66, 40, 22];
  function woodAt(x, y, top) {
    var dd = Math.max(0, y - top), k = Math.min(1, dd / 26), out = [];
    var g = 1 + 0.07 * Math.sin(x * 0.42 + 2.4 * Math.sin(y * 0.11 + x * 0.013)) + 0.04 * Math.sin(x * 1.7 + y * 0.3);
    for (var c = 0; c < 3; c++) out.push((dd < 2.2 ? WOOD_HI[c] : WOOD[c] + (WOOD_LO[c] - WOOD[c]) * k) * g);
    return out;
  }
  function bakeBar(r, win, F) {
    var W = r.width, H = r.height, d = r.data, m = win.mask, nx = r.hang.x, yt = r.hang.surfaceY, s = F / 900;
    var yo = -1, yb = -1, x, y, i, k, c;
    for (y = win.top; y <= win.bot; y++) if (m[y * W + nx]) { if (yo < 0) yo = y; yb = y; }
    if (yo < 0) return null;
    // deeper than the lab's search box for a crew's opening (0.12 of the frame): a scoop, a V, an open back
    var hw = Math.max(8 * s, (win.right - win.left) / 2), deep = yb - yt > 0.12 * F, ywc, c2, band;
    if (deep) { ywc = yo + 2 * s; c2 = 0.55 * 0.055 * F; band = 0.03 * F; }
    else { ywc = Math.max(yo + s, yt + 0.4 * (yb - yt)); c2 = 0.55 * (yb - yt); band = Infinity; }
    // distance to the opening's edge (chamfer, px): the front collar shades the wood near it
    var x0 = Math.max(0, win.left - 2), x1 = Math.min(W - 1, win.right + 2), y0 = Math.max(0, win.top - 2), y1 = Math.min(H - 1, win.bot + 2);
    var dist = new Float32Array(W * H), BIG = 1e6;
    for (y = y0; y <= y1; y++) for (x = x0; x <= x1; x++) { i = y * W + x; dist[i] = m[i] ? BIG : 0; }
    for (y = y0 + 1; y <= y1; y++) for (x = x0 + 1; x <= x1; x++) {
      i = y * W + x; if (dist[i]) dist[i] = Math.min(dist[i], dist[i - 1] + 1, dist[i - W] + 1, dist[i - W - 1] + 1.414, x < x1 ? dist[i - W + 1] + 1.414 : BIG);
    }
    for (y = y1 - 1; y >= y0; y--) for (x = x1 - 1; x >= x0; x--) {
      i = y * W + x; if (dist[i]) dist[i] = Math.min(dist[i], dist[i + 1] + 1, dist[i + W] + 1, dist[i + W + 1] + 1.414, x > x0 ? dist[i + W - 1] + 1.414 : BIG);
    }
    var sh = 5 * s;
    for (y = y0; y <= y1; y++) {
      for (x = x0; x <= x1; x++) {
        i = y * W + x; k = i * 4;
        var yw = ywc + c2 * Math.pow((x - nx) / hw, 2), inBar = y >= yw && y < yw + band, wv;
        if (m[i]) {
          if (inBar) {
            wv = woodAt(x / s, y / s, yw / s);
            var o = clamp(dist[i] / (6 * s), 0.45, 1);
            for (c = 0; c < 3; c++) d[k + c] = wv[c] * o;
          } else {
            var f = 0.9 - 0.2 * clamp((y - yt) / Math.max(4, yb - yt), 0, 1);
            if (y < yw && y > yw - sh) f *= 0.6 + 0.4 * (yw - y) / sh;
            for (c = 0; c < 3; c++) d[k + c] *= f;
          }
        } else if (inBar && d[k + 3] > 200 && (m[i - 1] || m[i + 1] || m[i - W] || m[i + W])) {
          // the opening's rim, half lining and half cloth: no light seam between the wood and the front collar
          wv = woodAt(x / s, y / s, yw / s);
          for (c = 0; c < 3; c++) d[k + c] = Math.min(d[k + c], wv[c] * 0.55);
        }
      }
    }
    return ywc / H;
  }

  // The whole piece from the photo's pixels: cut out (OTK_CUTOUT, the first IIFE above), then hung. job: { width,
  // height, data (the photo at the cut-out's size, RGBA), it }. null: not a flat shot. Anything odd in the hanging: the
  // piece as photographed.
  function piece(job) {
    var cut = root.OTK_CUTOUT, r = cut ? cut({ width: job.width, height: job.height, data: job.data }, { fast: true }) : null;
    if (!r || !r.flat || !r.data) return null;
    var c = null;
    try { c = hangPiece(r, job.width, job.it); } catch (e) { c = null; }
    return c || hangPiece(r, job.width, job.it, true);
  }

  OTK_HANG.K = HK;
  OTK_HANG.hangPiece = hangPiece;
  OTK_HANG.piece = piece;
  OTK_HANG.lib = otkHangLib; // (its source, for the rails' worker)
  root.OTK_HANG = OTK_HANG;
  // in the rails' worker (see hangWorker): a job in, the piece out (its pixels handed over, not copied)
  if (typeof root.importScripts === 'function' && typeof root.document === 'undefined') {
    root.onmessage = function (e) {
      var m = e.data || {}, c = null;
      try { c = piece(m.job); } catch (err) { root.postMessage({ id: m.id, fail: String(err && err.message || err) }); return; }
      root.postMessage({ id: m.id, res: c }, c && c.data ? [c.data.buffer] : []);
    };
  }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));

/* OTK Sakura hero, side niches: the pieces hung face-out on two rails (reference for rack-lab/design/SPEC.md).
   Contract (assets/otk-sakura-hero.js, mountRacks / stepRacks / onSides):
     var api = window.OTK_SAKURA_RACKS(showEl, { side: 'l' | 'r', still: false }); (the grade: --otk-shade on the section)
     api.step(dt, time, v)   every frame while the side is open; v = the hero's smoothed scroll speed x direction (-1..1)
     api.kick(dir, strength) the wall sliding away (dir -1 left / +1 right; strength = |change in open progress| x 9)
     api.layout()            re-place (the hero's crop changed); api.destroy()
   Until every piece is cut out (or if one fails) the lit display boxes stay. Geometry is in niche frame px
   (niche 287 x 737, origin top-left; 1 frame px = 1 --u). */
(function () {
  'use strict';

  var NW = 287;

  // cut: real length (cm) of the cut-out's box at size M (hood / straps included); bf: body fraction of its width
  // (the rest is sleeves or spread legs, which may tuck behind a neighbour); hs: hanger scale; waist: default
  // waistband fraction of the width (measured from the cut-out when there is one)
  var CUTS = {
    hoodie: { cm: 78, bf: 0.72, hs: 1.06 },
    jersey: { cm: 76, bf: 0.70, hs: 1.06 },
    wide: { cm: 72, bf: 0.60, hs: 1.06 },
    tee: { cm: 74, bf: 0.66, hs: 1.00 },
    tee_w: { cm: 64, bf: 0.66, hs: 0.94 },
    tank: { cm: 66, bf: 1.00, hs: 0.90 },
    crop: { cm: 50, bf: 0.42, hs: 0.90 },
    bra: { cm: 34, bf: 1.00, hs: 0.62 },
    legging: { cm: 94, bf: 1.00, waist: 0.60 },
    flare: { cm: 104, bf: 0.78, waist: 0.45 },
    pants: { cm: 100, bf: 0.90, waist: 0.55 },
    biker: { cm: 48, bf: 0.90, waist: 0.63 },
    shorts: { cm: 52, bf: 0.86, waist: 0.50 },
    skirt: { cm: 55, bf: 1.00, waist: 0.80 }
  };

  var K = {
    max2: 1.9,      // room scale, frame px per cm, two rails
    max1: 2.2,      // one rail (1-3 pieces)
    min: 1.15,
    fit: 1.0,       // below this width fit the niche shows its lit boxes instead (too narrow a strip in view)
    edge: 8,        // garments keep this far off the side walls / the crop line
    oMin: 0.06,     // neighbours always overlap a little (fraction of the narrower width)
    oCap: 0.36,     // and never more than this
    rowGap: 22,     // min clearance: upper hems -> lower hook tops
    zoneGap: 18,    // count line -> rack zone, rack zone -> button
    topDrop: 15,    // rail top -> collar, top hanger at scale 1 (viewBox P(40,6) -> H(40,21))
    clipDrop: 20.5, // rail top -> waistband, clip hanger at scale 1 (P(40,6) -> H(40,26.5))
    hookUp: 2       // the hook's curl rises this far above the rail top
  };

  // sway (angles in degrees, rotate(+) swings the hem to the left)
  var SWAY = {
    zeta: 0.14, zetaHeld: 0.6,   // damping ratio; a resting hand steadies the piece
    maxTop: 7, maxLong: 5,       // soft limits (long = 80 cm and over)
    wind: 2.5,                   // lean at full scroll speed, alternating along the rail
    gust: 0.5,                   // swing per unit of kick strength (a full wall opening adds up to ~4.5)
    gustSpeed: 700,              // the gust crosses the niche from the inner wall outward, frame px/s
    brush: 0.0025, brushMax: 3   // swing per frame px/s of pointer speed, and its cap
  };

  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function sum(a) { var s = 0; for (var i = 0; i < a.length; i++) s += a[i]; return s; }

  function classify(title) {
    var t = String(title || '').toLowerCase();
    var bottom = /legging|shorts|pants|jogger|skirt/.test(t);
    var women = /women|ladies|\bher\b/.test(t);
    var cut;
    if (bottom) {
      cut = /skirt/.test(t) ? 'skirt' : /biker/.test(t) ? 'biker' : /shorts/.test(t) ? 'shorts' : /flare/.test(t) ? 'flare'
        : /pants|jogger|sweatpant|trouser/.test(t) ? 'pants' : 'legging';
    } else {
      cut = /\bbra\b|bralette/.test(t) ? 'bra' : /crop/.test(t) ? 'crop' : /hoodie|sweatshirt|crewneck|pullover/.test(t) ? 'hoodie'
        : /jersey/.test(t) ? 'jersey' : /oversized|wide|boxy/.test(t) ? 'wide' : /tank|singlet/.test(t) ? 'tank' : women ? 'tee_w' : 'tee';
    }
    return { kind: bottom ? 'bottom' : 'top', cut: cut };
  }

  /* ------------------------------------------------------------------------------------------------ layout */
  // (hk: the hung cut-out's height for its photo's scale: sleeves hanging lower make it taller, a hood fallen back shorter)
  function dims(it, k) { var h = CUTS[it.cut].cm * (it.hk || 1) * k; return { w: h * it.ar, h: h }; }
  function bodyF(it) { return it.bf || CUTS[it.cut].bf; } // measured on the hung cut-out when it could be
  function drop(it, s) { return it.kind === 'top' ? K.topDrop * (CUTS[it.cut].hs || 1) * s : K.clipDrop * s; }
  function rowH(row, k) {
    var s = k / 1.9, m = 0;
    row.forEach(function (it) { m = Math.max(m, drop(it, s) + dims(it, k).h); });
    return K.hookUp * s + m;
  }
  function sleeves(it, w) { return (1 - bodyF(it)) / 2 * w; }
  function ovBounds(row, ws) {
    var lo = [], hi = [];
    for (var a = 0; a < row.length - 1; a++) {
      var m = Math.min(ws[a], ws[a + 1]);
      lo.push(K.oMin * m);
      hi.push(Math.min(K.oCap * m, sleeves(row[a], ws[a]) + sleeves(row[a + 1], ws[a + 1]) + 0.12 * m));
    }
    return { lo: lo, hi: hi };
  }

  // tops on the upper rail (at most 3), the rest below; 1-3 pieces hang on one rail
  function rails(items) {
    if (items.length <= 3) return [items.slice()];
    var tops = items.filter(function (i) { return i.kind === 'top'; });
    var bots = items.filter(function (i) { return i.kind !== 'top'; });
    var all = tops.concat(bots), up = Math.min(3, Math.max(tops.length, items.length - 3));
    return [all.slice(0, up), all.slice(up)];
  }

  // items: { kind, cut, ar }; box: { zl, zr, zoneT, zoneB } (frame px); side: 'l' | 'r'
  function layout(items, box, side) {
    var R = rails(items), zoneW = box.zr - box.zl, zoneH = box.zoneB - box.zoneT;
    var k = R.length === 1 ? K.max1 : K.max2;
    R.forEach(function (row) { // width: each row fits at its tightest overlap (end sleeves may tuck half past the zone).
      // Measured on the pieces as photographed (ar0): hung, they are narrower, and they keep their size and hang a
      // little further apart rather than growing to fill the rail
      row = row.map(function (it) { return { ar: it.ar0 || it.ar, cut: it.cut, kind: it.kind }; });
      var ws = row.map(function (it) { return dims(it, 1).w; }), ov = ovBounds(row, ws);
      var oh = row.length > 1 ? 0.5 * sleeves(row[0], ws[0]) + 0.5 * sleeves(row[row.length - 1], ws[ws.length - 1]) : 0;
      k = Math.min(k, zoneW / (sum(ws) - sum(ov.hi) - oh));
    });
    var kw = k, gap0 = R.length === 2 ? K.rowGap : 0, blk = sum(R.map(function (r) { return rowH(r, k); })) + gap0;
    if (blk > zoneH) k = k * (zoneH - gap0) / (blk - gap0); // height
    k = Math.min(kw, Math.max(K.min, k)); // a floor for legibility, but never wider than the zone
    var s = k / 1.9, rh = R.map(function (r) { return rowH(r, k); }), anchors;
    if (R.length === 2) {
      var spare = zoneH - (rh[0] + K.rowGap + rh[1]);
      var gap = K.rowGap + Math.min(48, Math.max(0, spare) * 0.3);
      var top = Math.max(0, (zoneH - (rh[0] + gap + rh[1])) * 0.42);
      anchors = [box.zoneT + top + K.hookUp * s];
      anchors.push(anchors[0] + rh[0] + gap);
    } else {
      anchors = [box.zoneT + Math.max(0, (zoneH - rh[0]) * 0.5) + K.hookUp * s];
    }
    var out = [];
    R.forEach(function (row, ri) {
      // the hangers are spaced along the rail as for the pieces as photographed (ar0, their body fractions as cut:
      // the layout the walls were designed with); each piece then hangs from its hanger at its hung width, narrower,
      // so a little wall shows between neighbours rather than the row bunching up in the middle of the rail
      var d = row.map(function (it) { return dims(it, k); }), ws = d.map(function (x) { return x.w; });
      var r0 = row.map(function (it) { return { ar: it.ar0 || it.ar, cut: it.cut, kind: it.kind }; });
      var w0 = r0.map(function (it, a) { return Math.max(ws[a], CUTS[it.cut].cm * k * it.ar); });
      var n = row.length, b = ovBounds(r0, w0), ov = b.lo, wLo = sum(w0) - sum(b.lo);
      if (n > 1 && wLo > zoneW) {
        var wHi = sum(w0) - sum(b.hi), t = Math.min(1, (wLo - zoneW) / Math.max(1e-6, wLo - wHi));
        ov = b.lo.map(function (lo, a) { return lo + t * (b.hi[a] - lo); });
      }
      var x = (box.zl + box.zr) / 2 - (sum(w0) - sum(ov)) / 2;
      var zs = n === 3 ? (side === 'l' ? [0, 2, 1] : [1, 2, 0]) : [0, 1]; // 3: the middle hangs in front
      row.forEach(function (it, a) {
        out.push({ it: it, rail: ri, x: x + w0[a] / 2, y: anchors[ri], w: ws[a], h: d[a].h, drop: drop(it, s),
          hz: s * (it.kind === 'top' ? (CUTS[it.cut].hs || 1) : 1), z: 4 + ri * 3 + zs[a] });
        x += w0[a] - (a < n - 1 ? ov[a] : 0);
      });
    });
    return { k: k, kw: kw, s: s, anchors: anchors, rails: R.length, pieces: out };
  }

  /* ------------------------------------------------------------------------------------------------ cut-out */
  // Each piece: the product's first flat shot (OTK_CUTOUT, above: flood-fills the white studio ground away, keeps the
  // garment's own whites, un-mixes the white from its edge, and tells a flat / ghost mockup from a shot on a model),
  // trimmed to the garment and hung (OTK_HANG.hangPiece: hung, measured, the drape's light, the walnut in a front's
  // opening). A replacement can be passed: opts.cutout(urls, item) -> Promise<{ url, ar, waist }>.
  var SRC = 520; // px on the long side

  // when the page is idle (the visitor may be scrolling the reveal right after load)
  function onIdle(f) {
    if (window.requestIdleCallback) window.requestIdleCallback(f, { timeout: 1500 });
    else setTimeout(f, 50);
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = function () {
        if (im.decode) im.decode().then(function () { resolve(im); }, function () { resolve(im); });
        else resolve(im);
      };
      im.onerror = reject;
      im.src = src;
    });
  }

  // the photo's pixels at SRC px on the long side (a transparent PNG gets the white ground the cut-out expects), and
  // what the hanging needs to know about the piece (url: the photo's name says front or back on a Printful mockup)
  function photoJob(im, it) {
    var w = im.naturalWidth, h = im.naturalHeight;
    if (!w || !h) return null;
    var sc = Math.min(1, SRC / Math.max(w, h)), cw = Math.round(w * sc), ch = Math.round(h * sc);
    var c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.fillStyle = '#fff';
    g.fillRect(0, 0, cw, ch);
    g.drawImage(im, 0, 0, cw, ch);
    it = it || {};
    return { width: cw, height: ch, data: g.getImageData(0, 0, cw, ch).data,
      it: { kind: it.kind, cut: it.cut, title: it.title || '', url: it.url || im.currentSrc || im.src || '' } };
  }

  function toCanvas(c) {
    var out = document.createElement('canvas');
    out.width = c.width; out.height = c.height;
    out.getContext('2d').putImageData(new ImageData(c.data, c.width, c.height), 0, 0);
    c.canvas = out;
    return c;
  }

  // The rack lab's way in (and the page's, one step at a time, below): the piece made here and now. flat: as
  // photographed. Returns OTK_HANG.hangPiece's result with its canvas, or null (no flat shot).
  function cutCanvas(im, it, flat) {
    var job = window.OTK_CUTOUT && window.OTK_HANG && photoJob(im, it);
    var r = job ? window.OTK_CUTOUT(job, { fast: true }) : null;
    if (!r || !r.flat || !r.data) return null;
    var c = null;
    try { c = window.OTK_HANG.hangPiece(r, job.width, job.it, flat); } catch (e) { c = null; }
    if (!c && !flat) c = window.OTK_HANG.hangPiece(r, job.width, job.it, true);
    return c ? toCanvas(c) : null;
  }

  // The cutting and the hanging are plain pixel work (OTK_CUTOUT and OTK_HANG are built for it: no page, no canvas), so
  // they run in a worker, off the page's thread: a phone scrolling the reveal never waits on them. The worker is made
  // from the two modules' own source (one Blob, no extra file), shared by both sides, and let go when it has had nothing
  // to do for a while. If it can't be made, fails, or takes too long, the page does the job itself, in idle moments.
  var HW = { w: null, url: null, off: !window.Worker || !window.Blob || !window.URL || !URL.createObjectURL, jobs: {}, n: 0, rest: 0 };
  function hangWorker() {
    if (HW.off) return null;
    if (HW.w) return HW.w;
    try {
      var src = '(' + window.OTK_CUTOUT.lib + ')(self);\n(' + window.OTK_HANG.lib + ')(self);\n';
      HW.url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
      HW.w = new Worker(HW.url);
    } catch (e) { workerOff(); return null; }
    HW.w.onmessage = function (e) {
      var m = e.data || {}, job = HW.jobs[m.id];
      if (!job) return;
      delete HW.jobs[m.id]; clearTimeout(job.t);
      if (m.fail) job.no(); else job.ok(m.res);
      rest();
    };
    HW.w.onerror = function (e) { if (e && e.preventDefault) e.preventDefault(); workerOff(); }; // (the page takes over)
    return HW.w;
  }
  function workerOff() {
    HW.off = true;
    if (HW.w) HW.w.terminate();
    if (HW.url) URL.revokeObjectURL(HW.url);
    HW.w = null; HW.url = null;
    var jobs = HW.jobs; HW.jobs = {};
    Object.keys(jobs).forEach(function (id) { clearTimeout(jobs[id].t); jobs[id].no(); });
  }
  function rest() { // nothing more to do for 8 s: let it go (a later piece makes a new one)
    clearTimeout(HW.rest);
    if (Object.keys(HW.jobs).length) return;
    HW.rest = setTimeout(function () {
      if (!HW.w || Object.keys(HW.jobs).length) return;
      HW.w.terminate(); URL.revokeObjectURL(HW.url); HW.w = null; HW.url = null;
    }, 8000);
  }
  function inWorker(job) {
    return new Promise(function (ok, no) {
      var w = hangWorker();
      if (!w) { no(); return; }
      clearTimeout(HW.rest);
      var id = ++HW.n;
      HW.jobs[id] = { ok: ok, no: no, t: setTimeout(function () { if (HW.jobs[id]) workerOff(); }, 15000) };
      try { w.postMessage({ id: id, job: job }, [job.data.buffer]); } catch (e) { workerOff(); }
    });
  }
  // the page's own way: the cut-out and its hanging as two idle tasks, so a slow phone never stalls for both at once
  function onPage(im, it) {
    return new Promise(function (resolve) {
      onIdle(function () {
        var job = photoJob(im, it), r = null;
        try { r = job && window.OTK_CUTOUT(job, { fast: true }); } catch (e) { r = null; }
        if (!r || !r.flat || !r.data) { resolve(null); return; }
        onIdle(function () {
          var c = null;
          try { c = window.OTK_HANG.hangPiece(r, job.width, job.it); } catch (e) { c = null; }
          if (!c) try { c = window.OTK_HANG.hangPiece(r, job.width, job.it, true); } catch (e) { c = null; } // as photographed
          resolve(c);
        });
      });
    });
  }

  // one photo -> Promise of the piece's blob URL and measurements, or of null (not a flat shot: try the next photo)
  function cutImage(im, it) {
    if (!window.OTK_CUTOUT || !window.OTK_HANG) return null;
    var job = photoJob(im, it);
    if (!job) return null;
    return inWorker(job).then(null, function () { return onPage(im, it); }).then(function (c) {
      if (!c || !c.data) return null;
      toCanvas(c);
      return new Promise(function (resolve) {
        c.canvas.toBlob(function (blob) {
          resolve(blob ? { url: URL.createObjectURL(blob), ar: c.ar, ar0: c.ar0, waist: c.waist, hx: c.hx, neck: c.neck, bar: c.bar,
            stem: c.stem, fw: c.fw, hk: c.hk, bf: c.bf, hood: c.hood, blob: true } : null);
        }, 'image/png');
      });
    });
  }

  function defaultCutout(urls, it) {
    var k = 0;
    function next() {
      if (k >= urls.length) return Promise.reject(new Error('no flat shot'));
      var src = urls[k++];
      return loadImage(src).then(function (im) {
        return new Promise(function (resolve) { onIdle(function () { resolve(im); }); }); // the photo's pixels, idle too
      }).then(function (im) {
        var r = cutImage(im, it);
        return r ? r.then(function (res) { return res || next(); }) : next();
      }, next);
    }
    return next();
  }

  /* ------------------------------------------------------------------------------------------------ markup */
  // the hangers (viewBox units = frame px at hanger scale 1; P (40,6) rests on the rail top; H = garment top:
  // (40,21) collar for the top hanger, (40,26.5) waistband for the clip hanger). Flat fills only: no ids to clash.
  // They match the middle rack's: a polished steel hook (dark edge, steel core, a bright streak on the lit side) and
  // walnut (otk-armory-lab's WOOD / WOOD_HI). The top hanger is inside its garment: the hook comes down behind the collar
  // (its neck reaches the garment's own collar, measured on the cut-out: --neck, viewBox units below the box top), and
  // what shows of the hanger is the middle rack's: on a front with a neck opening, the walnut bar painted into the opening
  // with a steel stem down to it over the back band (.otk-rk__neck, bakeBar); on a back view, the walnut boss peeking
  // above the collar where the hook screws in (.otk-rk__boss). A hood shows neither: from behind, the hook comes out over
  // its top; from the front, out of its neck, so its stem runs down over the hood to the neck (.otk-rk__neck--hood, as
  // on the middle rack). The hanger sits over the collar's centre, not the box's (--hx on the item, frame px).
  var STEEL_D = '#2b2c30', STEEL = '#8e929a', STEEL_HI = '#eef0f4';
  function steelStem(y, h, scaled) { // the hook's straight shank: three layers (edge, core, lit streak)
    var st = scaled ? ' style="transform-box:fill-box;transform-origin:50% 0;transform:scaleY(calc(1 + var(--neck, 0) / ' + h + '))"' : '';
    return '<rect x="39.05" y="' + y + '" width="1.9" height="' + h + '" fill="' + STEEL_D + '"' + st + '/>' +
      '<rect x="39.35" y="' + y + '" width="1.3" height="' + h + '" fill="' + STEEL + '"' + st + '/>' +
      '<rect x="39.55" y="' + y + '" width=".45" height="' + h + '" fill="' + STEEL_HI + '" opacity=".85"' + st + '/>';
  }
  function steelCurl(y1) { // the curl over the rail, from the shank's top at y1
    return '<path d="M40 ' + y1 + 'V8.7C40 6 41.5 5.05 43.1 5.05C44.5 5.05 45.2 5.8 45.35 6.7" fill="none" stroke="' + STEEL_D + '" stroke-width="1.9" stroke-linecap="round"/>' +
      '<path d="M40 ' + y1 + 'V8.7C40 6 41.5 5.05 43.1 5.05C44.5 5.05 45.2 5.8 45.35 6.7" fill="none" stroke="' + STEEL + '" stroke-width="1.3" stroke-linecap="round"/>' +
      '<path d="M39.78 ' + y1 + 'V8.75C39.8 6.1 41.3 4.85 43.05 4.85" fill="none" stroke="' + STEEL_HI + '" stroke-width=".45" stroke-linecap="round" opacity=".85"/>';
  }
  var HANGER_TOP =
    '<svg class="otk-rk__hanger otk-rk__hanger--top" viewBox="0 0 80 34" aria-hidden="true" focusable="false">' +
    steelStem(17, 6, true) + steelCurl(17.5) + '</svg>'; // the shank runs on 2 units into the collar (hidden behind it)
  var HANGER_CLIP =
    '<svg class="otk-rk__hanger otk-rk__hanger--clip" viewBox="0 0 80 36" aria-hidden="true" focusable="false">' +
    steelCurl(14.5) +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * 1px))">' +
    '<rect x="9" y="15.8" width="43" height="3.8" rx="1.9" fill="#704628"/></g>' +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * -1px))">' +
    '<rect x="28" y="15.8" width="43" height="3.8" rx="1.9" fill="#704628"/></g>' +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * 1px))">' +
    '<path d="M10.6 16.35H50.4" stroke="#b27c50" stroke-width=".6" stroke-linecap="round"/>' +
    '<path d="M10.8 19.1H50.2" stroke="#42281a" stroke-width=".5" stroke-linecap="round" opacity=".8"/></g>' +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * -1px))">' +
    '<path d="M29.6 16.35H69.4" stroke="#b27c50" stroke-width=".6" stroke-linecap="round"/>' +
    '<path d="M29.8 19.1H69.2" stroke="#42281a" stroke-width=".5" stroke-linecap="round" opacity=".8"/></g>' +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * 1px))">' +
    '<rect x="12.4" y="18.8" width="7.2" height="2.6" rx=".9" fill="#191715"/>' +
    '<path d="M12.8 21.2H19.2L18.7 31.2C18.6 32 18.1 32.5 17.3 32.5H14.7C13.9 32.5 13.4 32 13.3 31.2Z" fill="#1e1c1a"/>' +
    '<path d="M14.2 22.6V30.6" stroke="#7a6654" stroke-width=".5" stroke-linecap="round" opacity=".8"/>' +
    '<path d="M13.5 29.4H18.5" stroke="#0b0a09" stroke-width=".55"/></g>' +
    '<g style="transform:translateX(calc(var(--clip-in, 0) * -1px))">' +
    '<rect x="60.4" y="18.8" width="7.2" height="2.6" rx=".9" fill="#191715"/>' +
    '<path d="M60.8 21.2H67.2L66.7 31.2C66.6 32 66.1 32.5 65.3 32.5H62.7C61.9 32.5 61.4 32 61.3 31.2Z" fill="#1e1c1a"/>' +
    '<path d="M62.2 22.6V30.6" stroke="#7a6654" stroke-width=".5" stroke-linecap="round" opacity=".8"/>' +
    '<path d="M61.5 29.4H66.5" stroke="#0b0a09" stroke-width=".55"/></g>' +
    '<rect x="38.5" y="14.4" width="3" height="1.6" rx=".5" fill="#c9ccd2"/></svg>';

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // (the cut-out is --cut on the swing: the garment's light and its shadow on the wall are both cut to its shape)
  function pieceHTML(it) {
    var top = it.kind === 'top';
    return '<li class="otk-rk__item" data-kind="' + it.kind + '">' +
      '<a class="otk-rk__piece" href="' + esc(it.href) + '" aria-label="' + esc(it.name + ', ' + it.price) + '">' +
      '<span class="otk-rk__lift"><span class="otk-rk__swing" style="--cut: url(\'' + esc(it.src) + '\')">' +
      '<span class="otk-rk__shadow" aria-hidden="true"></span>' + (top ? HANGER_TOP : '') +
      (top && it.boss ? '<span class="otk-rk__boss"></span>' : '') + // behind the garment: only its top shows
      '<span class="otk-rk__fig"><img class="otk-rk__img" src="' + esc(it.src) +
      '" alt="" decoding="async"></span>' +
      (top && it.bar != null ? '<span class="otk-rk__neck"></span>' : '') +
      (top && it.bar == null && it.stem != null ? '<span class="otk-rk__neck otk-rk__neck--hood"><span class="otk-rk__stem"></span></span>' : '') +
      (top ? '' : HANGER_CLIP) + '</span>' +
      '<span class="otk-rk__tag" aria-hidden="true"><span class="otk-rk__name">' + esc(it.name) + '</span>' +
      '<span class="otk-rk__price">' + esc(it.price) + '</span></span></span>' +
      '<span class="otk-rk__hit"></span></a></li>';
  }

  /* ------------------------------------------------------------------------------------------------ the rack */
  function mount(show, opts) {
    opts = opts || {};
    var sideEl = show.closest('.otk-sakura__side');
    var side = opts.side || (sideEl && sideEl.getAttribute('data-side')) || 'l';
    var still = !!opts.still || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var dead = false, built = false, abandoned = false, fits = true, racks = null, ro = null, L = null, u = 1, clock = 0;
    var tagged = null, lastType = '', tapFlag = 0;
    function unflag() { clearTimeout(tapFlag); if (sideEl) sideEl.removeAttribute('data-rk-refocus'); }
    var items = Array.prototype.slice.call(show.querySelectorAll('.otk-sakura__piece')).map(function (a, i) {
      var nameEl = a.querySelector('.otk-sakura__piece-name'), priceEl = a.querySelector('.otk-sakura__piece-price');
      var name = nameEl ? nameEl.textContent.trim() : '', title = a.getAttribute('data-title') || name;
      var c = classify(title), kind = a.getAttribute('data-kind') || c.kind;
      if (kind !== c.kind) c.cut = kind === 'top' ? 'tee' : 'legging';
      var r = Math.sin((i + 1) * 12.9898) * 43758.5453;
      return {
        box: a, href: a.getAttribute('href'), name: name, title: title, price: priceEl ? priceEl.textContent.trim() : '', kind: kind, cut: c.cut,
        imgs: (a.getAttribute('data-imgs') || '').split('|').filter(Boolean),
        gain: 0.75 + 0.5 * (r - Math.floor(r)), dir: i % 2 ? -1 : 1, a: 0, v: 0, queue: [], held: false
      };
    });
    var cut = opts.cutout || defaultCutout;

    function cropPx(name) { return parseFloat(getComputedStyle(sideEl).getPropertyValue(name)) || 0; }

    function measure() {
      var r = sideEl.getBoundingClientRect();
      if (!r.width) return null;
      u = r.width / NW;
      var count = show.querySelector('.otk-sakura__show-count'), cta = show.querySelector('.otk-sakura__show-cta');
      var crop = cropPx('--otk-crop') / u;
      var padB = parseFloat(getComputedStyle(show).paddingBottom) || 78 * u; // clear of a cropped bottom (CSS)
      return {
        zl: side === 'l' ? Math.max(K.edge, crop + K.edge) : K.edge,
        zr: side === 'l' ? NW - K.edge : NW - Math.max(K.edge, crop + K.edge),
        zoneT: (count.getBoundingClientRect().bottom - r.top) / u + K.zoneGap,
        zoneB: ((cta ? cta.getBoundingClientRect().top : r.bottom - padB) - r.top) / u - K.zoneGap
      };
    }

    // racked (true) or back to the lit boxes (false): a strip of the niche too narrow for the rails shows the boxes,
    // which shrink to fit
    function setFits(ok) {
      if (ok === fits) return;
      fits = ok;
      racks.hidden = !ok;
      sideEl.classList.toggle('otk-sakura__side--racked', ok);
      if (!ok) untag();
    }

    function place() {
      if (!built || dead) return;
      var box = measure();
      if (!box) return;
      L = layout(items, box, side);
      if (L.kw >= K.fit !== fits) {
        setFits(L.kw >= K.fit);
        if (fits && (box = measure())) L = layout(items, box, side); // the racked niche lays its text out a little differently
      }
      if (!fits || !box) return;
      // the tags' real widths (laid out while hidden), read before any write
      var tws = L.pieces.map(function (p) { var t = p.it.li.querySelector('.otk-rk__tag'); return t ? t.offsetWidth / u : 0; });
      var railEls = racks.querySelectorAll('.otk-rk__rail');
      for (var i = 0; i < railEls.length; i++) {
        railEls[i].hidden = i >= L.rails;
        if (i < L.rails) railEls[i].style.setProperty('--y', L.anchors[i].toFixed(2));
      }
      L.pieces.forEach(function (p, j) {
        var it = p.it, st = it.li.style;
        st.setProperty('--x', p.x.toFixed(2));
        st.setProperty('--y', p.y.toFixed(2));
        st.setProperty('--w', p.w.toFixed(2));
        st.setProperty('--h', p.h.toFixed(2));
        st.setProperty('--drop', p.drop.toFixed(2));
        st.setProperty('--hz', p.hz.toFixed(4));
        st.setProperty('--bf', Math.max(0.5, bodyF(it)).toFixed(2));
        st.setProperty('--z', p.z);
        it.li.setAttribute('data-rail', L.rails === 1 ? 'only' : p.rail ? 'lower' : 'upper');
        if (it.kind === 'top') { // the hook over the collar's centre, its neck down into the collar (see HANGER_TOP)
          st.setProperty('--hx', ((it.hx - 0.5) * p.w).toFixed(2));
          st.setProperty('--neck', (it.neck * p.h / p.hz).toFixed(2));
          st.setProperty('--collar', (p.drop + it.neck * p.h).toFixed(2));
          if (it.bar != null) st.setProperty('--bar', Math.max(0, (it.bar - it.neck) * p.h).toFixed(2)); // collar -> wood
          else if (it.stem != null) st.setProperty('--bar', Math.max(0, (it.stem - it.neck) * p.h).toFixed(2)); // a hood's top -> its neck
          if (it.boss) { // the lab's boss: 0.024 x 0.016 of the Printful frame's width (at least a few hook widths here)
            var wf = (it.fw || 1.25) * p.w;
            st.setProperty('--brx', Math.max(0.024 * wf, 3.4 * p.hz).toFixed(2));
            st.setProperty('--bry', Math.max(0.016 * wf, 2.3 * p.hz).toFixed(2));
          }
        } else { // the clips slide along the bar to the waistband (hanger viewBox units, + = inward)
          var waistVb = it.waist * p.w / p.hz;
          st.setProperty('--clip-in', clamp((48 - 0.84 * waistVb) / 2, -6, 12).toFixed(2));
        }
        // the tag keeps inside the zone (0.52: half of the hover lift's 1.04 scale); wider than the zone: centred
        var half = 0.52 * (tws[j] || Math.min(170, Math.max(70, p.w))), dx = 0;
        if (2 * half >= box.zr - box.zl) dx = (box.zl + box.zr) / 2 - p.x;
        else if (p.x - half < box.zl) dx = box.zl - (p.x - half);
        else if (p.x + half > box.zr) dx = box.zr - (p.x + half);
        st.setProperty('--tag-dx', dx.toFixed(2));
        var cm = CUTS[it.cut].cm;
        it.w0 = Math.sqrt(9.81 / (0.0062 * cm + 0.06)); // a physical pendulum the length of the piece
        it.amax = cm >= 80 ? SWAY.maxLong : SWAY.maxTop;
        it.x = p.x;
      });
    }

    // pointer brushing past: its x speed when it enters / leaves a piece
    var px = 0, pt = 0, pvx = 0;
    function onMove(e) {
      if (e.pointerType !== 'mouse') return;
      var now = e.timeStamp || performance.now();
      if (pt && now > pt) pvx = 0.6 * pvx + 0.4 * ((e.clientX - px) / (now - pt) * 1000);
      px = e.clientX; pt = now;
    }
    function brush(it, leaving) {
      if (still) return;
      var amp = Math.min(SWAY.brushMax, Math.abs(pvx / u) * SWAY.brush) * (leaving ? 0.5 : 1);
      it.v += -Math.sign(pvx) * amp * (it.w0 || 4.6); // a rightward brush swings the hem right: rotate(-)
    }

    // The piece being shown (a first tap, the pointer on it, keyboard focus), or none (null), told to the hero as
    // 'otk:rk-tag' (bubbling from the niche; detail: side, name, price, link and its cut-out, or null): on phones and
    // tablets showing the whole room the garments are small, so the hero shows their name and price at a readable size
    // under the room (otk-sakura-hero.js bandTag). Nothing listens for it on desktop.
    var told = null;
    function tell(it) {
      if (it === told || !sideEl) return;
      told = it;
      var d = it ? { side: side, name: it.name, price: it.price, href: it.href, img: it.src || '' } : null;
      sideEl.dispatchEvent(new CustomEvent('otk:rk-tag', { bubbles: true, detail: d }));
    }
    // (the pointer leaving a piece, or its focus: back to the piece tapped, if it isn't this one, or to none)
    function untell(it) { if (told === it && tagged !== it) tell(tagged); }

    // touch: the first tap on a piece shows its name and price, a second tap opens it (as on the live rack)
    function untag() {
      if (tagged) { var was = tagged; tagged.li.classList.remove('is-tag'); tagged = null; untell(was); }
    }
    function onDocDown(e) {
      // (a tap on the hero's plate for the piece, under the room on phones, is a tap on the piece: it opens it)
      if (tagged && !tagged.li.contains(e.target) && !(e.target.closest && e.target.closest('[data-otk-band-tag]'))) untag();
    }

    function build() {
      if (dead) return;
      racks = document.createElement('div');
      racks.className = 'otk-sakura__racks';
      // the list in the order the pieces hang (upper rail left to right, then the lower one): reading and tab order
      var ordered = [].concat.apply([], rails(items));
      racks.innerHTML = '<span class="otk-rk__rail" aria-hidden="true"></span><span class="otk-rk__rail" aria-hidden="true"></span>' +
        '<ul class="otk-rk__list" role="list">' + ordered.map(pieceHTML).join('') + '</ul>';
      var lis = racks.querySelectorAll('.otk-rk__item');
      ordered.forEach(function (it, i) {
        it.li = lis[i];
        it.sw = lis[i].querySelector('.otk-rk__swing');
        var a = lis[i].querySelector('.otk-rk__piece');
        a.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { brush(it); it.held = true; tell(it); } });
        a.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { it.held = false; brush(it, true); untell(it); } });
        a.addEventListener('focus', function () { tell(it); });
        a.addEventListener('blur', function () { untell(it); });
        a.addEventListener('pointerdown', function (e) {
          lastType = e.pointerType;
          // a tapped piece is in view already: its focus mustn't scroll the hero to the wall (which would slide the
          // piece from under the finger, so a second tap lands on a neighbour). The flag tells the hero's focusin.
          if (lastType === 'touch' || lastType === 'pen') { sideEl.setAttribute('data-rk-refocus', ''); clearTimeout(tapFlag); tapFlag = setTimeout(unflag, 1200); }
        });
        a.addEventListener('pointercancel', function () { lastType = ''; unflag(); }); // a touch that became a scroll
        a.addEventListener('click', function (e) {
          var touch = e.detail > 0 && (lastType === 'touch' || lastType === 'pen'); // (a keyboard click has detail 0)
          lastType = ''; // a keyboard Enter (no pointer) goes straight through
          unflag();
          if (!touch || tagged === it) return;
          e.preventDefault();
          untag();
          tagged = it;
          it.li.classList.add('is-tag');
          tell(it);
        });
      });
      sideEl.addEventListener('pointermove', onMove, { passive: true }); // over the whole niche: fresh speed on entering
      document.addEventListener('pointerdown', onDocDown, true);
      // keyboard focus on a box moves to the same piece on the rails
      var fa = document.activeElement, back = null;
      if (fa && show.contains(fa)) items.forEach(function (it) { if (it.box === fa || it.box.contains(fa)) back = it; });
      // after the heading and count, before the button: the reading order matches what is seen
      show.insertBefore(racks, show.querySelector('.otk-sakura__show-cta'));
      sideEl.classList.add('otk-sakura__side--racked');
      built = true;
      fits = true;
      place();
      if (back && fits) { // (flagged so the hero doesn't scroll to the open wall for it)
        sideEl.setAttribute('data-rk-refocus', '');
        back.li.querySelector('.otk-rk__piece').focus({ preventScroll: true });
        sideEl.removeAttribute('data-rk-refocus');
      }
      if (window.ResizeObserver) { ro = new ResizeObserver(function () { place(); }); ro.observe(sideEl); }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { place(); }); // tag widths
    }

    function revokeAll() {
      items.forEach(function (it) { if (it.blob && it.src) URL.revokeObjectURL(it.src); it.src = null; it.blob = false; });
    }

    // one piece at a time, each when the page is idle (the visitor may be scrolling the reveal right after load)
    var idle = onIdle, next = 0;
    (function cutNext() {
      if (dead || abandoned) return;
      if (next >= items.length) { build(); return; }
      var it = items[next++];
      idle(function () {
        if (dead) return;
        cut(it.imgs, it).then(function (r) {
          if (dead || abandoned) { if (r && r.blob) URL.revokeObjectURL(r.url); return; }
          it.src = r.url; it.ar = r.ar; it.ar0 = r.ar0 || r.ar; it.waist = r.waist || CUTS[it.cut].waist || 0.6; it.blob = r.blob;
          it.hx = r.hx == null ? 0.5 : r.hx; it.neck = r.neck || 0; it.bar = r.bar == null ? null : r.bar; it.fw = r.fw;
          it.stem = r.stem == null || it.bar != null ? null : r.stem; // a hood seen from the front: the stem over it
          it.hk = r.hk > 0 ? r.hk : 1; it.bf = r.bf || 0; // hung: its box's height for the same scale, the body's share
          it.boss = it.kind === 'top' && it.bar == null && !r.hood && !/hood/i.test(it.title); // as on the middle rack: no boss on a hood
          cutNext();
        }, function () { abandoned = true; revokeAll(); }); // a piece couldn't be cut out: the side keeps its boxes
      });
    })();

    return {
      layout: function () { place(); return L; },
      // every frame while this side is open and the hero on screen: v = scroll speed x direction, -1..1
      step: function (dt, time, v) {
        if (!built || still || !fits) return;
        var n = dt > 0.02 ? 2 : 1, h = dt / n, wind = SWAY.wind * clamp(v || 0, -1, 1);
        for (var sstep = 0; sstep < n; sstep++) {
          clock += h;
          items.forEach(function (it) {
            for (var q = it.queue.length - 1; q >= 0; q--) if (clock >= it.queue[q].at) { it.v += it.queue[q].dv; it.queue.splice(q, 1); }
            var w = it.w0 || 4.6, z = it.held ? SWAY.zetaHeld : SWAY.zeta;
            it.v += (-w * w * (it.a - wind * it.dir * it.gain) - 2 * z * w * it.v) * h;
            it.a += it.v * h;
            if (Math.abs(it.a) > it.amax) { it.a = Math.sign(it.a) * it.amax; it.v *= -0.35; }
          });
        }
        items.forEach(function (it) {
          if (Math.abs(it.a) < 0.01 && Math.abs(it.v) < 0.05 && !it.queue.length) { it.a = 0; it.v = 0; }
          var t = it.a ? 'rotate(' + it.a.toFixed(3) + 'deg)' : '';
          if (t !== it.t) { it.t = t; it.sw.style.transform = t; }
        });
      },
      // the wall sliding away: a gust that crosses from the inner wall outward, swinging the hems after the wall
      kick: function (dir, strength) {
        if (!built || still || !fits) return;
        items.forEach(function (it) {
          if (it.queue.length > 24) it.queue.shift(); // (never more than a wall's worth waiting)
          var fromInner = side === 'l' ? NW - (it.x || 0) : (it.x || 0);
          it.queue.push({ at: clock + fromInner / SWAY.gustSpeed, dv: -dir * SWAY.gust * (strength || 0) * it.gain * (it.w0 || 4.6) });
        });
      },
      destroy: function () {
        dead = true;
        unflag();
        if (ro) ro.disconnect();
        document.removeEventListener('pointerdown', onDocDown, true);
        if (sideEl) sideEl.removeEventListener('pointermove', onMove, { passive: true });
        if (racks && racks.parentNode) racks.parentNode.removeChild(racks);
        if (sideEl) sideEl.classList.remove('otk-sakura__side--racked');
        revokeAll();
      }
    };
  }

  var api = function (show, opts) { return mount(show, opts); };
  api.CUTS = CUTS; api.K = K; api.SWAY = SWAY; api.classify = classify; api.rails = rails; api.layout = layout;
  api.cutCanvas = cutCanvas; // (the rack lab renders the pieces with it, hung and as photographed)
  window.OTK_SAKURA_RACKS = api;
  if (typeof module !== 'undefined') module.exports = api;
})();
